import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Shield, Users, Mic2, PlayCircle, Megaphone, 
  Settings, Plus, Trash2, BarChart3, Lock, Music, Instagram, Facebook,
  Building2, CreditCard, DollarSign, CheckCircle, CheckCircle2, XCircle, Ban, Unlock, Eye, Key, Image as ImageIcon, Wallet, Gift, Banknote, Camera,
  Layers, Rocket, Youtube, Share2, Star, ToggleLeft, ToggleRight, MapPin, Crown, X, Upload, Clock, ClipboardList, Loader2, TrendingUp, ChevronUp, ChevronDown
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Point value per inbox action type (admin buzón display + default approval amount)
const ACTION_POINT_VALUES = { combo: 20, suscriptor: 15, like_comentario: 5, comentario: 3, like: 2, visita: 1 };
const ACTION_LABELS = { combo: "Combo", suscriptor: "Suscripción", like_comentario: "Like + Comentario", comentario: "Comentario", like: "Like", visita: "Visita" };

const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

// Icon options for carousel slides
const ICON_OPTIONS = [
  { value: 'rocket', label: '🚀 Cohete', icon: Rocket },
  { value: 'youtube', label: '📺 YouTube', icon: Youtube },
  { value: 'share', label: '📢 Compartir', icon: Share2 },
  { value: 'star', label: '⭐ Estrella', icon: Star },
  { value: 'music', label: '🎵 Música', icon: Music }
];

// Gradient options for carousel slides
const GRADIENT_OPTIONS = [
  { value: 'from-[#FF6B6B] via-[#FF8E53] to-[#FFD700]', label: 'Naranja/Dorado' },
  { value: 'from-[#FF0000] via-[#CC0000] to-[#990000]', label: 'Rojo (YouTube)' },
  { value: 'from-[#E1306C] via-[#F77737] to-[#FCAF45]', label: 'Rosa/Naranja' },
  { value: 'from-[#8B5CF6] via-[#D946EF] to-[#EC4899]', label: 'Morado/Rosa' },
  { value: 'from-[#22C55E] via-[#16A34A] to-[#15803D]', label: 'Verde' },
  { value: 'from-[#3B82F6] via-[#2563EB] to-[#1D4ED8]', label: 'Azul' }
];

const AdminPage = () => {
  const navigate = useNavigate();
  const { token, isAdmin, isAuthenticated } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState("all"); // "all", "pro", "recluta"
  const [pendingProRequests, setPendingProRequests] = useState([]);
  const [pendingRegistrations, setPendingRegistrations] = useState({ users: [], artists: [] });
  const [announcements, setAnnouncements] = useState([]);
  const [radioTracks, setRadioTracks] = useState([]);
  const [radioSubmissions, setRadioSubmissions] = useState([]);
  const [socialTasks, setSocialTasks] = useState([]);
  const [artistSocialSubmissions, setArtistSocialSubmissions] = useState([]);
  const [artistPayments, setArtistPayments] = useState([]);
  const [pendingMemberships, setPendingMemberships] = useState([]);
  const [artistsMembershipStatus, setArtistsMembershipStatus] = useState([]);
  const [artistLinks, setArtistLinks] = useState([]);
  const [inboxSubmissions, setInboxSubmissions] = useState([]);
  const [engagementOrders, setEngagementOrders] = useState([]);
  const [selectedOrderForSub, setSelectedOrderForSub] = useState({});
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [pendingScreenshots, setPendingScreenshots] = useState([]);
  const [pendingTaskScreenshots, setPendingTaskScreenshots] = useState([]);
  const [carouselSlides, setCarouselSlides] = useState([]);
  const [galleryArtworks, setGalleryArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("subscriptions");
  const [showAddAnnouncement, setShowAddAnnouncement] = useState(false);
  const [showAddRadio, setShowAddRadio] = useState(false);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [showAddSocial, setShowAddSocial] = useState(false);
  const [showAddCarousel, setShowAddCarousel] = useState(false);
  const [showAddGallery, setShowAddGallery] = useState(false);
  const [galleryForm, setGalleryForm] = useState({ title: "", artist_name: "", description: "", art_type: "poster" });
  const [galleryFile, setGalleryFile] = useState(null);
  const [uploadingGallery, setUploadingGallery] = useState(false);

  const [announcementForm, setAnnouncementForm] = useState({ title: "", content: "", image_url: "" });
  const [radioForm, setRadioForm] = useState({ title: "", artist_name: "", mp3_url: "", cover_url: "", daily_plays: 10 });
  const [radioFile, setRadioFile] = useState(null);
  const [adminForm, setAdminForm] = useState({ phone: "", name: "", password: "" });
  const [socialForm, setSocialForm] = useState({ platform: "", username: "", profile_url: "", description: "" });
  const [carouselForm, setCarouselForm] = useState({
    title: "",
    subtitle: "",
    description: "",
    slide_type: "promo",
    bg_gradient: "from-[#8B5CF6] via-[#D946EF] to-[#EC4899]",
    icon: "rocket",
    youtube_url: "",
    image_url: "",
    artist_id: "",
    launch_datetime: ""
  });
  const [whatsappConfig, setWhatsappConfig] = useState({ number: "", message: "" });
  const [blockReason, setBlockReason] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [viewingScreenshot, setViewingScreenshot] = useState(null);
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [artistBalanceRequests, setArtistBalanceRequests] = useState([]);
  const [showBonusDialog, setShowBonusDialog] = useState(false);
  const [bonusForm, setBonusForm] = useState({ user_id: "", amount: "", reason: "" });
  const [bonuses, setBonuses] = useState([]);
  const [allArtists, setAllArtists] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, userId: null, userName: "", type: "user" });
  
  // Artist password reset state
  const [showArtistResetPassword, setShowArtistResetPassword] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [artistNewPassword, setArtistNewPassword] = useState("");
  
  // Artist launch datetime state
  const [showArtistLaunchDate, setShowArtistLaunchDate] = useState(false);
  const [artistLaunchDateTime, setArtistLaunchDateTime] = useState("");
  
  // Carousel slide datetime state
  const [showSlideDatetime, setShowSlideDatetime] = useState(false);
  const [selectedSlideId, setSelectedSlideId] = useState(null);
  const [slideDatetime, setSlideDatetime] = useState("");
  
  // Artist photo upload state
  const [showArtistPhotoUpload, setShowArtistPhotoUpload] = useState(false);
  const [artistPhotoFile, setArtistPhotoFile] = useState(null);
  const [uploadingArtistPhoto, setUploadingArtistPhoto] = useState(false);
  
  // Location stats state
  const [locationStats, setLocationStats] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationUsers, setLocationUsers] = useState([]);
  
  // Assigned tasks state
  const [reclutas, setReclutas] = useState([]);
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [showAssignTask, setShowAssignTask] = useState(false);
  const [showBulkAssignTask, setShowBulkAssignTask] = useState(false);
  const [assignTaskForm, setAssignTaskForm] = useState({
    user_id: "",
    title: "",
    description: "",
    reward: 50,
    task_url: ""
  });
  const [bulkAssignForm, setBulkAssignForm] = useState({
    user_count: 30,
    title: "",
    description: "",
    reward: 50,
    task_url: ""
  });
  
  // Daily visits state
  const [dailyVisitConfigs, setDailyVisitConfigs] = useState([]);
  const [dailyVisitSubmissions, setDailyVisitSubmissions] = useState([]);
  const [showAddDailyVisit, setShowAddDailyVisit] = useState(false);
  const [dailyVisitForm, setDailyVisitForm] = useState({
    visit_type: "youtube",
    target_url: "",
    platform: "youtube",
    title: "",
    reward_amount: 10,
    daily_target: 50
  });
  const [maxVisitsPerUser, setMaxVisitsPerUser] = useState(5);
  
  // Launch submissions state
  const [launchSubmissions, setLaunchSubmissions] = useState([]);
  const [launchRewardAmounts, setLaunchRewardAmounts] = useState({});
  
  // All launches state (for admin management)
  const [allLaunches, setAllLaunches] = useState([]);
  
  // Telegram state
  const [telegramStats, setTelegramStats] = useState({ total_subscribers: 0, active_subscribers: 0 });
  const [sendingTelegramAlert, setSendingTelegramAlert] = useState(false);
  
  // Points Bank state
  const [pointsBank, setPointsBank] = useState({ total_points: 0, total_earnings: 0, transactions: [] });
  const [bankEarnings, setBankEarnings] = useState({ today: 0, today_date: "", history: [] });
  const [sellArtistId, setSellArtistId] = useState("");
  const [sellPointsAmount, setSellPointsAmount] = useState("");
  const [sellingPoints, setSellingPoints] = useState(false);
  
  const [paymentAccounts, setPaymentAccounts] = useState({
    rd: { bank: "", account: "", name: "", rnc: "", type: "Cuenta Corriente" },
    usd: { bank: "", account: "", name: "", swift: "", type: "Cuenta Corriente" }
  });

  // Auto-refresh for daily visit submissions
  const refreshDailyVisitSubmissions = async () => {
    try {
      const res = await axios.get(`${API}/admin/daily-visits/submissions?status=pending`, { headers: { Authorization: `Bearer ${token}` } });
      setDailyVisitSubmissions(res.data);
    } catch (e) {
      console.log("Error refreshing submissions");
    }
  };

  // Auto-refresh for launch submissions
  const refreshLaunchSubmissions = async () => {
    try {
      const res = await axios.get(`${API}/admin/launch-submissions?status=pending`, { headers: { Authorization: `Bearer ${token}` } });
      setLaunchSubmissions(res.data);
    } catch (e) {
      console.log("Error refreshing launch submissions");
    }
  };

  // Handle review launch submission
  const handleReviewLaunchSubmission = async (submissionId, approved) => {
    try {
      const rewardAmount = launchRewardAmounts[submissionId] || 0;
      if (approved && rewardAmount <= 0) {
        toast.error("Ingresa un monto de recompensa válido");
        return;
      }
      await axios.put(
        `${API}/admin/launch-submissions/${submissionId}/review?approved=${approved}&reward_amount=${approved ? rewardAmount : 0}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(approved ? `Captura aprobada - RD$${rewardAmount} pagado` : "Captura rechazada");
      refreshLaunchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al revisar captura");
    }
  };

  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      fetchAllData();
      fetchPendingRegistrations();
    }
  }, [isAuthenticated, isAdmin, token]);

  const fetchPendingRegistrations = async () => {
    try {
      const res = await axios.get(`${API}/admin/pending-registrations`, { headers: { Authorization: `Bearer ${token}` } });
      setPendingRegistrations(res.data || { users: [], artists: [] });
    } catch (e) {
      setPendingRegistrations({ users: [], artists: [] });
    }
  };

  const handleApproveRegistration = async (kind, id, name) => {
    try {
      const path = kind === "artist" ? `artists/${id}/approve-registration` : `users/${id}/approve-registration`;
      await axios.put(`${API}/admin/${path}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Registro de ${name || ''} aprobado`);
      setPendingRegistrations(prev => ({
        users: kind === "user" ? prev.users.filter(u => u.id !== id) : prev.users,
        artists: kind === "artist" ? prev.artists.filter(a => a.id !== id) : prev.artists,
      }));
      fetchAllData(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aprobar registro");
    }
  };

  const handleRejectRegistration = async (kind, id, name) => {
    if (!window.confirm(`¿Rechazar y ELIMINAR el registro de ${name || 'este registro'}? Esta acción no se puede deshacer.`)) return;
    try {
      const path = kind === "artist" ? `artists/${id}/delete` : `users/${id}/delete`;
      await axios.delete(`${API}/admin/${path}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Registro de ${name || ''} rechazado y eliminado`);
      setPendingRegistrations(prev => ({
        users: kind === "user" ? prev.users.filter(u => u.id !== id) : prev.users,
        artists: kind === "artist" ? prev.artists.filter(a => a.id !== id) : prev.artists,
      }));
      fetchAllData(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al rechazar registro");
    }
  };

  // Auto-refresh submissions every 30 seconds
  useEffect(() => {
    if (isAuthenticated && isAdmin && token) {
      const interval = setInterval(() => {
        refreshDailyVisitSubmissions();
        refreshLaunchSubmissions();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, isAdmin, token]);

  const fetchAllData = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const [statsRes, usersRes, annRes, radioRes, whatsappRes, socialRes, accountsRes, artistPaymentsRes, userSubsRes, withdrawalsRes, artistBalanceRes, bonusesRes, artistsRes, screenshotsRes, carouselRes, taskScreenshotsRes, reclutasRes, assignedTasksRes, radioSubsRes, galleryRes, locationRes, artistSocialRes, proRequestsRes, dailyVisitConfigsRes, dailyVisitSubmissionsRes, launchSubmissionsRes, telegramStatsRes, allLaunchesRes, membershipsPendingRes, membershipsStatusRes, artistLinksRes, inboxRes, engagementRes, pointsBankRes, bankEarningsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/announcements`),
        axios.get(`${API}/radio/tracks`),
        axios.get(`${API}/config/whatsapp`),
        axios.get(`${API}/social-tasks`),
        axios.get(`${API}/config/payment-accounts`),
        axios.get(`${API}/admin/artist-payments/pending`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/subscriptions/pending`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/withdrawals/pending`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/artist-balance-requests/pending`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/bonuses`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/artists`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/screenshots/pending`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/carousel/slides`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/task-screenshots/pending`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/users/reclutas`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/assigned-tasks`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/radio/submissions/pending`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/gallery/artworks`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/stats/locations`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { summary: {}, locations: [] } })),
        axios.get(`${API}/admin/artist-social-submissions/pending`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/pro-requests/pending`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/daily-visits/configs`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/daily-visits/submissions?status=pending`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/launch-submissions?status=pending`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/telegram/stats`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { total_subscribers: 0, active_subscribers: 0 } })),
        axios.get(`${API}/admin/launch-events/all`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/membership/pending`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/membership/artists-status`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/link-submissions`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/inbox/submissions`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/engagement/orders`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/points-bank`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { total_points: 0, total_earnings: 0, transactions: [] } })),
        axios.get(`${API}/admin/points-bank/daily-earnings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { today: 0, today_date: "", history: [] } }))
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      setAnnouncements(annRes.data);
      setRadioTracks(radioRes.data);
      setWhatsappConfig(whatsappRes.data);
      setSocialTasks(socialRes.data);
      setPaymentAccounts(accountsRes.data);
      setArtistPayments(artistPaymentsRes.data);
      setUserSubscriptions(userSubsRes.data);
      setPendingWithdrawals(withdrawalsRes.data);
      setArtistBalanceRequests(artistBalanceRes.data);
      setBonuses(bonusesRes.data);
      setAllArtists(artistsRes.data.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      setPendingScreenshots(screenshotsRes.data);
      setCarouselSlides(carouselRes.data);
      setPendingTaskScreenshots(taskScreenshotsRes.data);
      setReclutas(reclutasRes.data.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      setAssignedTasks(assignedTasksRes.data);
      setRadioSubmissions(radioSubsRes.data);
      setGalleryArtworks(galleryRes.data);
      setLocationStats(locationRes.data);
      setArtistSocialSubmissions(artistSocialRes.data);
      setPendingProRequests(proRequestsRes.data);
      setDailyVisitConfigs(dailyVisitConfigsRes.data);
      setDailyVisitSubmissions(dailyVisitSubmissionsRes.data);
      setLaunchSubmissions(launchSubmissionsRes.data);
      setTelegramStats(telegramStatsRes.data);
      setAllLaunches(allLaunchesRes.data);
      setPendingMemberships(membershipsPendingRes.data);
      setArtistsMembershipStatus(membershipsStatusRes.data);
      setArtistLinks(artistLinksRes.data);
      setInboxSubmissions(inboxRes.data);
      setEngagementOrders(engagementRes.data);
      setPointsBank(pointsBankRes.data);
      setBankEarnings(bankEarningsRes.data);
      
      // Get daily visit settings
      try {
        const settingsRes = await axios.get(`${API}/admin/daily-visits/settings`, { headers: { Authorization: `Bearer ${token}` } });
        setMaxVisitsPerUser(settingsRes.data.max_visits_per_user_per_day || 5);
      } catch (e) {
        console.log("No daily visit settings found, using default");
      }
    } catch (error) {
      console.error("Error fetching admin data:", error);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  // Carousel functions
  const handleAddCarouselSlide = async (e) => {
    e.preventDefault();
    try {
      let imageUrl = carouselForm.image_url;
      
      // If there's a photo file, upload it first
      if (carouselForm.photoFile) {
        const formData = new FormData();
        formData.append("file", carouselForm.photoFile);
        
        const uploadRes = await axios.post(
          `${API}/admin/carousel/upload-image`,
          formData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data"
            }
          }
        );
        imageUrl = uploadRes.data.image_url;
      }
      
      // Prepare the form data
      const slideData = {
        title: carouselForm.title,
        subtitle: carouselForm.subtitle,
        description: carouselForm.description,
        slide_type: carouselForm.slide_type,
        bg_gradient: carouselForm.bg_gradient,
        icon: carouselForm.icon,
        youtube_url: carouselForm.youtube_url || "",
        image_url: imageUrl || "",
        launch_datetime: carouselForm.launch_datetime ? new Date(carouselForm.launch_datetime).toISOString() : null,
        artist_id: carouselForm.artist_id || null
      };
      await axios.post(`${API}/admin/carousel/slides`, slideData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Slide agregado al carrusel");
      setShowAddCarousel(false);
      setCarouselForm({ title: "", subtitle: "", description: "", slide_type: "promo", bg_gradient: "from-[#8B5CF6] via-[#D946EF] to-[#EC4899]", icon: "rocket", youtube_url: "", image_url: "", artist_id: "", launch_datetime: "", photoFile: null });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al agregar slide");
    }
  };

  const handleDeleteCarouselSlide = async (slideId) => {
    try {
      await axios.delete(`${API}/admin/carousel/slides/${slideId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Slide eliminado");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar slide");
    }
  };

  const handleMoveSlide = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= carouselSlides.length) return;
    const reordered = [...carouselSlides];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setCarouselSlides(reordered); // optimistic
    try {
      await Promise.all(reordered.map((s, i) =>
        axios.put(`${API}/admin/carousel/slides/${s.id}/order?new_order=${i + 1}`, {}, { headers: { Authorization: `Bearer ${token}` } })
      ));
      toast.success("Orden del carrusel actualizado");
      fetchAllData(false);
    } catch (error) {
      toast.error("Error al cambiar el orden");
      fetchAllData(false);
    }
  };

  const handleToggleCarouselSlide = async (slideId) => {
    try {
      const response = await axios.put(`${API}/admin/carousel/slides/${slideId}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar estado");
    }
  };

  const handleUpdateSlideDatetime = async () => {
    if (!selectedSlideId) return;
    try {
      await axios.put(
        `${API}/admin/carousel/slides/${selectedSlideId}/datetime`,
        { launch_datetime: slideDatetime ? new Date(slideDatetime).toISOString() : null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(slideDatetime ? "Fecha/hora actualizada" : "Fecha/hora eliminada");
      setShowSlideDatetime(false);
      setSelectedSlideId(null);
      setSlideDatetime("");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar fecha");
    }
  };

  // Daily visits functions
  const handleAddDailyVisit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/daily-visits/config`, dailyVisitForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Visita diaria creada");
      setShowAddDailyVisit(false);
      setDailyVisitForm({ visit_type: "youtube", target_url: "", platform: "youtube", title: "", reward_amount: 10, daily_target: 50 });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear visita");
    }
  };

  const handleDeleteDailyVisit = async (configId) => {
    try {
      await axios.delete(`${API}/admin/daily-visits/config/${configId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Visita eliminada");
      setDailyVisitConfigs(prev => prev.filter(c => c.id !== configId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleToggleDailyVisit = async (configId) => {
    try {
      const response = await axios.put(`${API}/admin/daily-visits/config/${configId}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      setDailyVisitConfigs(prev => prev.map(c => c.id === configId ? { ...c, is_active: response.data.is_active } : c));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar estado");
    }
  };

  const handleReviewDailyVisit = async (submissionId, approved) => {
    try {
      await axios.put(`${API}/admin/daily-visits/submission/${submissionId}/review?approved=${approved}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(approved ? "Visita aprobada" : "Visita rechazada");
      setDailyVisitSubmissions(prev => prev.filter(s => s.id !== submissionId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al revisar");
    }
  };

  const handleUpdateMaxVisits = async (newMax) => {
    try {
      await axios.put(`${API}/admin/daily-visits/settings?max_visits=${newMax}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setMaxVisitsPerUser(newMax);
      toast.success(`Límite actualizado a ${newMax} visitas por usuario por día`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar límite");
    }
  };

  // Screenshot approval functions
  const handleApproveScreenshot = async (screenshotId) => {
    try {
      const response = await axios.put(`${API}/admin/screenshots/${screenshotId}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      // Update local state
      setPendingScreenshots(prev => prev.filter(s => s.id !== screenshotId));
    } catch (error) {
      console.error("Approve screenshot error:", error);
      toast.error(error.response?.data?.detail || "Error al aprobar");
    }
  };

  const handleRejectScreenshot = async (screenshotId, reason = "") => {
    try {
      const response = await axios.put(`${API}/admin/screenshots/${screenshotId}/reject?reason=${encodeURIComponent(reason)}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      // Update local state
      setPendingScreenshots(prev => prev.filter(s => s.id !== screenshotId));
    } catch (error) {
      console.error("Reject screenshot error:", error);
      toast.error(error.response?.data?.detail || "Error al rechazar");
    }
  };

  // Pro request approval functions
  const handleApproveProRequest = async (requestId) => {
    try {
      const response = await axios.put(`${API}/admin/pro-requests/${requestId}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      // Update local state
      setPendingProRequests(prev => prev.filter(r => r.id !== requestId));
    } catch (error) {
      console.error("Approve pro request error:", error);
      toast.error(error.response?.data?.detail || "Error al aprobar solicitud Pro");
    }
  };

  const handleRejectProRequest = async (requestId) => {
    try {
      const response = await axios.put(`${API}/admin/pro-requests/${requestId}/reject`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      // Update local state
      setPendingProRequests(prev => prev.filter(r => r.id !== requestId));
    } catch (error) {
      console.error("Reject pro request error:", error);
      toast.error(error.response?.data?.detail || "Error al rechazar solicitud Pro");
    }
  };

  const handleCancelPro = async (userId, userName) => {
    if (!window.confirm(`¿Cancelar el Pro de ${userName} y regresarlo a Recluta?`)) return;
    try {
      const response = await axios.put(`${API}/admin/users/${userId}/cancel-pro`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, user_tier: "recluta", pro_status: "none" } : u));
      setPendingProRequests(prev => prev.filter(r => r.user_id !== userId));
    } catch (error) {
      console.error("Cancel pro error:", error);
      toast.error(error.response?.data?.detail || "Error al cancelar Pro");
    }
  };

  // Task screenshot approval functions
  const handleApproveTaskScreenshot = async (screenshotId) => {
    try {
      const response = await axios.put(`${API}/admin/task-screenshots/${screenshotId}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      // Update local state
      setPendingTaskScreenshots(prev => prev.filter(s => s.id !== screenshotId));
    } catch (error) {
      console.error("Approve task screenshot error:", error);
      toast.error(error.response?.data?.detail || "Error al aprobar");
    }
  };

  const handleRejectTaskScreenshot = async (screenshotId, reason = "") => {
    try {
      const response = await axios.put(`${API}/admin/task-screenshots/${screenshotId}/reject?reason=${encodeURIComponent(reason)}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al rechazar");
    }
  };

  // Assigned task functions
  const handleAssignTask = async (e) => {
    e.preventDefault();
    if (!assignTaskForm.user_id || !assignTaskForm.title || !assignTaskForm.description) {
      toast.error("Completa todos los campos requeridos");
      return;
    }
    try {
      await axios.post(`${API}/admin/assign-task`, assignTaskForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Tarea asignada exitosamente");
      setShowAssignTask(false);
      setAssignTaskForm({ user_id: "", title: "", description: "", reward: 50, task_url: "" });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al asignar tarea");
    }
  };

  // Bulk assign task to multiple users
  const handleBulkAssignTask = async (e) => {
    e.preventDefault();
    if (!bulkAssignForm.title || !bulkAssignForm.description || bulkAssignForm.user_count < 1) {
      toast.error("Completa todos los campos requeridos");
      return;
    }
    try {
      const response = await axios.post(`${API}/admin/assign-task-bulk`, bulkAssignForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message || `Tarea asignada a ${response.data.assigned_count} usuarios`);
      setShowBulkAssignTask(false);
      setBulkAssignForm({ user_count: 30, title: "", description: "", reward: 50, task_url: "" });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al asignar tareas en lote");
    }
  };

  const handleApproveAssignedTask = async (taskId) => {
    try {
      const response = await axios.put(`${API}/admin/assigned-tasks/${taskId}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      // Update local state
      setAssignedTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: 'approved' } : t));
    } catch (error) {
      console.error("Approve task error:", error);
      toast.error(error.response?.data?.detail || "Error al aprobar tarea");
    }
  };

  const handleRejectAssignedTask = async (taskId) => {
    try {
      const response = await axios.put(`${API}/admin/assigned-tasks/${taskId}/reject`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      // Update local state
      setAssignedTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: 'rejected' } : t));
    } catch (error) {
      console.error("Reject task error:", error);
      toast.error(error.response?.data?.detail || "Error al rechazar tarea");
    }
  };

  const handleDeleteAssignedTask = async (taskId) => {
    try {
      await axios.delete(`${API}/admin/assigned-tasks/${taskId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Tarea eliminada");
      // Update local state immediately without full refresh
      setAssignedTasks(prev => prev.filter(t => t.id !== taskId));
    } catch (error) {
      console.error("Delete task error:", error);
      toast.error(error.response?.data?.detail || "Error al eliminar tarea");
    }
  };

  const handleAddAnnouncement = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/announcements`, announcementForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Anuncio creado exitosamente");
      setShowAddAnnouncement(false);
      setAnnouncementForm({ title: "", content: "", image_url: "" });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear anuncio");
    }
  };

  const handleDeleteAnnouncement = async (annId) => {
    try {
      await axios.delete(`${API}/announcements/${annId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Anuncio eliminado");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar anuncio");
    }
  };

  const handleAddRadioTrack = async (e) => {
    e.preventDefault();
    try {
      if (radioFile) {
        // Upload MP3 file
        const formData = new FormData();
        formData.append("file", radioFile);
        formData.append("title", radioForm.title);
        formData.append("artist_name", radioForm.artist_name);
        formData.append("daily_plays", radioForm.daily_plays);
        
        await axios.post(`${API}/radio/upload-mp3`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });
        toast.success("Canción MP3 subida exitosamente");
      } else if (radioForm.mp3_url) {
        // Use URL method
        await axios.post(`${API}/radio/tracks`, radioForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Canción agregada a la radio");
      } else {
        toast.error("Sube un archivo MP3 o proporciona una URL");
        return;
      }
      
      setShowAddRadio(false);
      setRadioForm({ title: "", artist_name: "", mp3_url: "", cover_url: "", daily_plays: 10 });
      setRadioFile(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al agregar canción");
    }
  };

  const handleUpdateDailyPlays = async (trackId, newPlays) => {
    try {
      await axios.put(`${API}/radio/tracks/${trackId}/daily-plays?daily_plays=${newPlays}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Reproducciones actualizadas");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar");
    }
  };

  const handleDeleteRadioTrack = async (trackId) => {
    try {
      await axios.delete(`${API}/radio/tracks/${trackId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Canción eliminada de la radio");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar canción");
    }
  };

  const handleApproveRadioSubmission = async (submissionId) => {
    try {
      const response = await axios.put(
        `${API}/admin/radio/submissions/${submissionId}/approve?daily_plays=10`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aprobar");
    }
  };

  const handleRejectRadioSubmission = async (submissionId) => {
    const reason = prompt("Razón del rechazo (opcional):", "No cumple con los requisitos");
    if (reason === null) return; // User cancelled
    
    try {
      const response = await axios.put(
        `${API}/admin/radio/submissions/${submissionId}/reject?reason=${encodeURIComponent(reason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al rechazar");
    }
  };

  // Artist Social Submissions functions
  const handleApproveArtistSocial = async (submissionId) => {
    try {
      const response = await axios.put(
        `${API}/admin/artist-social-submissions/${submissionId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aprobar");
    }
  };

  const handleRejectArtistSocial = async (submissionId) => {
    const reason = prompt("Razón del rechazo (opcional):", "No cumple con los requisitos");
    if (reason === null) return;
    
    try {
      const response = await axios.put(
        `${API}/admin/artist-social-submissions/${submissionId}/reject?reason=${encodeURIComponent(reason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al rechazar");
    }
  };

  // Gallery functions
  const handleUploadGalleryArtwork = async (e) => {
    e.preventDefault();
    if (!galleryFile) {
      toast.error("Selecciona una imagen");
      return;
    }
    
    setUploadingGallery(true);
    try {
      const formData = new FormData();
      formData.append("file", galleryFile);
      formData.append("title", galleryForm.title);
      formData.append("artist_name", galleryForm.artist_name);
      formData.append("description", galleryForm.description);
      formData.append("art_type", galleryForm.art_type);
      
      const response = await axios.post(`${API}/admin/gallery/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(response.data.message);
      setShowAddGallery(false);
      setGalleryForm({ title: "", artist_name: "", description: "", art_type: "poster" });
      setGalleryFile(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir imagen");
    } finally {
      setUploadingGallery(false);
    }
  };

  const handleDeleteGalleryArtwork = async (artworkId) => {
    if (!window.confirm("¿Eliminar este arte?")) return;
    
    try {
      await axios.delete(`${API}/admin/gallery/artworks/${artworkId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Arte eliminado");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleToggleGalleryArtwork = async (artworkId, isActive) => {
    try {
      await axios.put(
        `${API}/admin/gallery/artworks/${artworkId}?is_active=${!isActive}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isActive ? "Arte ocultado" : "Arte visible");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar");
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/register`, adminForm);
      toast.success("Administrador creado exitosamente");
      setShowAddAdmin(false);
      setAdminForm({ phone: "", name: "", password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear administrador");
    }
  };

  const handleAddSocialTask = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/social-tasks`, socialForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Tarea de redes sociales creada");
      setShowAddSocial(false);
      setSocialForm({ platform: "", username: "", profile_url: "", description: "" });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear tarea");
    }
  };

  const handleDeleteSocialTask = async (taskId) => {
    try {
      await axios.delete(`${API}/social-tasks/${taskId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Tarea eliminada");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar tarea");
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case "instagram": return <Instagram className="w-5 h-5 text-[#E1306C]" />;
      case "facebook": return <Facebook className="w-5 h-5 text-[#1877F2]" />;
      case "tiktok": return <TikTokIcon className="w-5 h-5 text-white" />;
      default: return null;
    }
  };

  const handleUpdateWhatsapp = async () => {
    try {
      await axios.put(
        `${API}/config/whatsapp?number=${whatsappConfig.number}&message=${encodeURIComponent(whatsappConfig.message)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Configuración de WhatsApp actualizada");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar WhatsApp");
    }
  };

  const handleUpdatePaymentAccounts = async () => {
    try {
      const params = new URLSearchParams({
        rd_bank: paymentAccounts.rd.bank,
        rd_account: paymentAccounts.rd.account,
        rd_name: paymentAccounts.rd.name,
        rd_rnc: paymentAccounts.rd.rnc,
        rd_type: paymentAccounts.rd.type,
        usd_bank: paymentAccounts.usd.bank,
        usd_account: paymentAccounts.usd.account,
        usd_name: paymentAccounts.usd.name,
        usd_swift: paymentAccounts.usd.swift,
        usd_type: paymentAccounts.usd.type
      });
      
      await axios.put(`${API}/config/payment-accounts?${params}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Cuentas de pago actualizadas");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar cuentas");
    }
  };

  const handleApproveArtistPayment = async (paymentId) => {
    try {
      await axios.put(`${API}/admin/artist-payments/${paymentId}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Pago aprobado exitosamente");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aprobar pago");
    }
  };

  const handleApproveMembership = async (paymentId, defaultPoints = 250) => {
    const input = window.prompt(
      `¿Cuántos puntos acreditar al artista?\n(Por defecto ${defaultPoints} = 250 por cada mes/RD$300. Auméntalo si compró puntos adicionales.)`,
      String(defaultPoints)
    );
    if (input === null) return; // admin cancelled
    const points = parseInt(input, 10);
    if (isNaN(points) || points < 0) {
      toast.error("Cantidad de puntos inválida");
      return;
    }
    try {
      await axios.put(`${API}/admin/membership/${paymentId}/approve?points=${points}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Pago aprobado. ${points} puntos acreditados.`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aprobar membresía");
    }
  };

  const handleRejectMembership = async (paymentId) => {
    const reason = window.prompt("Motivo del rechazo (opcional):") || "";
    try {
      await axios.put(`${API}/admin/membership/${paymentId}/reject?reason=${encodeURIComponent(reason)}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Membresía rechazada");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al rechazar membresía");
    }
  };

  const handleAddPoints = async (artistId, artistName) => {
    const input = window.prompt(`¿Cuántos puntos agregar a ${artistName}?\n(Usa un número negativo para restar.)`, "250");
    if (input === null) return;
    const points = parseInt(input, 10);
    if (isNaN(points)) {
      toast.error("Cantidad de puntos inválida");
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/artists/${artistId}/add-points?points=${points}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message || "Puntos actualizados");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al agregar puntos");
    }
  };

  const handleSetArtistCode = async (artistId, artistName, currentCode) => {
    const input = window.prompt(`Código para ${artistName} (identifica al artista):`, currentCode || "");
    if (input === null) return;
    const code = input.trim();
    if (!code) {
      toast.error("El código no puede estar vacío");
      return;
    }
    try {
      const res = await axios.put(`${API}/admin/artists/${artistId}/code?code=${encodeURIComponent(code)}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message || "Código asignado");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al asignar código");
    }
  };

  const handleApproveInbox = async (submissionId, orderId, defaultPoints) => {
    const suggested = defaultPoints != null ? String(defaultPoints) : "20";
    const input = window.prompt("¿Cuántos puntos acreditar al usuario por esta captura?", suggested);
    if (input === null) return;
    const points = parseInt(input, 10);
    if (isNaN(points) || points < 0) {
      toast.error("Cantidad de puntos inválida");
      return;
    }
    const scrollY = window.scrollY;
    try {
      const orderParam = orderId ? `&order_id=${orderId}` : "";
      await axios.put(`${API}/admin/inbox/submissions/${submissionId}/approve?points=${points}${orderParam}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Envío aprobado. ${points} puntos acreditados.`);
      // Optimistic local update (approved submissions disappear from admin buzón) — keeps scroll stable
      setInboxSubmissions(prev => prev.filter(s => s.id !== submissionId));
      if (orderId) {
        setEngagementOrders(prev => prev.map(o => o.id === orderId ? { ...o, received: (o.received || 0) + 1 } : o));
      }
      requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo({ top: scrollY })));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aprobar envío");
    }
  };

  const handleRejectInbox = async (submissionId) => {
    const reason = window.prompt("Motivo del rechazo (opcional):") || "";
    const scrollY = window.scrollY;
    try {
      await axios.put(`${API}/admin/inbox/submissions/${submissionId}/reject?reason=${encodeURIComponent(reason)}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Envío rechazado");
      // Optimistic local update — mark as rejected in place, keeps scroll stable
      setInboxSubmissions(prev => prev.map(s => s.id === submissionId ? { ...s, status: 'rejected', rejection_reason: reason } : s));
      requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo({ top: scrollY })));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al rechazar envío");
    }
  };
  const handleDeleteInbox = async (submissionId) => {
    if (!window.confirm("¿Eliminar esta captura rechazada? Esta acción no se puede deshacer.")) return;
    const scrollY = window.scrollY;
    try {
      await axios.delete(`${API}/admin/inbox/submissions/${submissionId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Captura eliminada");
      setInboxSubmissions(prev => prev.filter(s => s.id !== submissionId));
      requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo({ top: scrollY })));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar captura");
    }
  };
  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm("¿Eliminar este pedido de engagement? Esta acción no se puede deshacer.")) return;
    const scrollY = window.scrollY;
    try {
      await axios.delete(`${API}/admin/engagement/orders/${orderId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Pedido eliminado");
      setEngagementOrders(prev => prev.filter(o => o.id !== orderId));
      requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo({ top: scrollY })));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar pedido");
    }
  };
  const handleSellPointsToArtist = async () => {
    if (!sellArtistId) {
      toast.error("Selecciona un artista");
      return;
    }
    const points = parseInt(sellPointsAmount, 10);
    if (isNaN(points) || points <= 0) {
      toast.error("Ingresa una cantidad válida de puntos");
      return;
    }
    setSellingPoints(true);
    try {
      const res = await axios.post(`${API}/admin/points-bank/sell-to-artist?artist_id=${sellArtistId}&points=${points}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      setSellArtistId("");
      setSellPointsAmount("");
      fetchAllData(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al vender puntos");
    } finally {
      setSellingPoints(false);
    }
  };


  const handleResetEarnings = async () => {
    if (!window.confirm("¿Reiniciar las Ganancias a 0 desde ahora? Se conservan todas las transacciones, pero las ganancias se contarán solo a partir de este momento.")) return;
    try {
      const res = await axios.post(`${API}/admin/points-bank/reset-earnings`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      fetchAllData(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al reiniciar las ganancias");
    }
  };


  const handleMarkLinkReviewed = async (submissionId) => {
    try {
      await axios.put(`${API}/admin/link-submissions/${submissionId}/reviewed`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Enlace marcado como revisado");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar enlace");
    }
  };

  const handleDeleteLink = async (submissionId) => {
    if (!window.confirm("¿Eliminar este enlace?")) return;
    try {
      await axios.delete(`${API}/admin/link-submissions/${submissionId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Enlace eliminado");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar enlace");
    }
  };

  const handleApproveUserSubscription = async (paymentId) => {
    try {
      await axios.put(`${API}/admin/subscriptions/${paymentId}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Inscripción aprobada exitosamente");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aprobar inscripción");
    }
  };

  const handleCancelUserSubscription = async (paymentId, userName) => {
    if (!window.confirm(`¿Cancelar la inscripción a Pro de ${userName || 'este usuario'}?`)) return;
    try {
      await axios.put(`${API}/admin/subscriptions/${paymentId}/cancel`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Inscripción cancelada");
      setUserSubscriptions(prev => prev.filter(s => s.id !== paymentId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cancelar inscripción");
    }
  };

  const handleBlockUser = async () => {
    if (!blockReason.trim()) {
      toast.error("Ingresa el motivo del bloqueo");
      return;
    }
    try {
      await axios.put(
        `${API}/admin/users/${selectedUser.id}/block?reason=${encodeURIComponent(blockReason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Usuario ${selectedUser.name} bloqueado`);
      setShowBlockDialog(false);
      setBlockReason("");
      setSelectedUser(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al bloquear usuario");
    }
  };

  const handleUnblockUser = async (userId, userName) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}/unblock`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Usuario ${userName} desbloqueado`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al desbloquear usuario");
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword.trim() || newPassword.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    try {
      await axios.put(
        `${API}/admin/users/${selectedUser.id}/reset-password?new_password=${encodeURIComponent(newPassword)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Contraseña actualizada para ${selectedUser.name}`);
      setShowResetPassword(false);
      setNewPassword("");
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al resetear contraseña");
    }
  };

  const handleProcessWithdrawal = async (withdrawalId, action) => {
    try {
      await axios.put(
        `${API}/admin/withdrawals/${withdrawalId}/process?action=${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(action === "approve" ? "Retiro aprobado" : "Retiro rechazado");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al procesar retiro");
    }
  };

  const handleApproveArtistBalance = async (requestId) => {
    try {
      await axios.put(
        `${API}/admin/artist-balance-requests/${requestId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Saldo agregado al artista");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aprobar recarga");
    }
  };

  const handleGiveBonus = async (e) => {
    e.preventDefault();
    if (!bonusForm.user_id || !bonusForm.amount || !bonusForm.reason) {
      toast.error("Completa todos los campos");
      return;
    }
    try {
      await axios.post(
        `${API}/admin/bonus`,
        {
          user_id: bonusForm.user_id,
          amount: parseFloat(bonusForm.amount),
          reason: bonusForm.reason
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Bono otorgado exitosamente");
      setShowBonusDialog(false);
      setBonusForm({ user_id: "", amount: "", reason: "" });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al otorgar bono");
    }
  };

  // Telegram functions
  const handleSendTelegramTestAlert = async () => {
    setSendingTelegramAlert(true);
    try {
      const response = await axios.post(
        `${API}/telegram/test-alert`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || "Alerta de prueba enviada");
      fetchAllData(); // Refresh stats
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar alerta");
    } finally {
      setSendingTelegramAlert(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    setDeleteConfirm({ show: true, userId, userName, type: "user" });
  };

  const confirmDelete = async () => {
    const { userId, userName, type } = deleteConfirm;
    setDeleteConfirm({ show: false, userId: null, userName: "", type: "user" });
    
    try {
      if (type === "user") {
        await axios.delete(`${API}/admin/users/${userId}/delete`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Cuenta de ${userName} eliminada`);
        // Update local state instead of full refresh
        setUsers(prev => prev.filter(u => u.id !== userId));
        setReclutas(prev => prev.filter(u => u.id !== userId));
      } else {
        await axios.delete(`${API}/admin/artists/${userId}/delete`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Cuenta del artista ${userName} eliminada`);
        // Update local state
        setAllArtists(prev => prev.filter(a => a.id !== userId));
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.response?.data?.detail || "Error al eliminar cuenta");
    }
  };

  const handleDeleteArtist = async (artistId, artistName) => {
    setDeleteConfirm({ show: true, userId: artistId, userName: artistName, type: "artist" });
  };

  // Delete launch event
  const handleDeleteLaunch = async (launchId, launchTitle) => {
    if (!window.confirm(`¿Estás seguro de eliminar el lanzamiento "${launchTitle}"?`)) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/launch-events/${launchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lanzamiento eliminado");
      setAllLaunches(prev => prev.filter(l => l.id !== launchId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar lanzamiento");
    }
  };

  const handleResetArtistPassword = async () => {
    if (!artistNewPassword || artistNewPassword.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    
    try {
      const response = await axios.put(
        `${API}/admin/artists/${selectedArtist.id}/reset-password?new_password=${encodeURIComponent(artistNewPassword)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setShowArtistResetPassword(false);
      setSelectedArtist(null);
      setArtistNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar contraseña");
    }
  };

  const handleUpdateArtistLaunchDate = async () => {
    try {
      const launchDatetime = artistLaunchDateTime ? new Date(artistLaunchDateTime).toISOString() : null;
      await axios.put(
        `${API}/admin/artists/${selectedArtist.id}/launch-datetime`,
        null,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { launch_datetime: launchDatetime }
        }
      );
      toast.success(launchDatetime ? "Fecha de lanzamiento actualizada" : "Fecha eliminada - Mostrará PRONTO");
      setShowArtistLaunchDate(false);
      setSelectedArtist(null);
      setArtistLaunchDateTime("");
      // Refresh artists list
      const artistsRes = await axios.get(`${API}/admin/artists`, { headers: { Authorization: `Bearer ${token}` } });
      setAllArtists(artistsRes.data.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar fecha");
    }
  };

  const handleUploadArtistPhoto = async () => {
    if (!artistPhotoFile || !selectedArtist) {
      toast.error("Selecciona una imagen");
      return;
    }
    
    setUploadingArtistPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", artistPhotoFile);
      
      const response = await axios.post(
        `${API}/admin/artists/${selectedArtist.id}/photo`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );
      
      toast.success("Foto actualizada");
      setShowArtistPhotoUpload(false);
      setSelectedArtist(null);
      setArtistPhotoFile(null);
      
      // Refresh artists list
      const artistsRes = await axios.get(`${API}/admin/artists`, { headers: { Authorization: `Bearer ${token}` } });
      setAllArtists(artistsRes.data.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir foto");
    } finally {
      setUploadingArtistPhoto(false);
    }
  };

  const handleDeleteArtistPhoto = async (artist) => {
    if (!window.confirm(`¿Eliminar la foto de ${artist.name}?`)) return;
    
    try {
      await axios.delete(
        `${API}/admin/artists/${artist.id}/photo`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Foto eliminada");
      
      // Refresh artists list
      const artistsRes = await axios.get(`${API}/admin/artists`, { headers: { Authorization: `Bearer ${token}` } });
      setAllArtists(artistsRes.data.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar foto");
    }
  };

  // Group inbox submissions by artist, with counts + that artist's engagement orders progress
  // NOTE: must be declared BEFORE any early return to satisfy the Rules of Hooks.
  const groupedInbox = useMemo(() => {
    const groups = {};
    (inboxSubmissions || []).forEach(s => {
      const key = s.artist_id || "sin_artista";
      if (!groups[key]) {
        groups[key] = {
          artist_id: s.artist_id || null,
          artist_name: s.artist_name || "Sin artista",
          artist_code: s.artist_code || "",
          subs: [],
        };
      }
      groups[key].subs.push(s);
    });
    const arr = Object.values(groups).map(g => {
      // pending first, then most recent
      g.subs.sort((a, b) => {
        if (a.status === "pending" && b.status !== "pending") return -1;
        if (a.status !== "pending" && b.status === "pending") return 1;
        return new Date(b.created_at) - new Date(a.created_at);
      });
      g.pendingCount = g.subs.filter(x => x.status === "pending").length;
      g.totalCount = g.subs.length;
      g.orders = (engagementOrders || []).filter(o => o.artist_id === g.artist_id);
      return g;
    });
    // artists with pending submissions first, then by total
    arr.sort((a, b) => (b.pendingCount - a.pendingCount) || (b.totalCount - a.totalCount));
    return arr;
  }, [inboxSubmissions, engagementOrders]);

  if (!isAuthenticated || !isAdmin) {
    return (
      <div className="min-h-screen pb-20 p-4 animate-fadeInUp flex items-center justify-center" data-testid="admin-page-restricted">
        <Card className="bg-[#121212] border-white/10 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Lock className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Acceso Restringido</h2>
            <p className="text-gray-400 mb-4">Solo los administradores pueden acceder a este panel</p>
            <Button onClick={() => navigate("/usuario")} className="bg-[#D946EF] hover:bg-[#D946EF]/80">
              Iniciar Sesión como Admin
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D946EF]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20 p-4 animate-fadeInUp" data-testid="admin-page">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-[#8B5CF6]/20">
            <Shield className="w-8 h-8 text-[#8B5CF6]" />
          </div>
          <div>
            <h1 className="text-3xl font-bold" style={{
              background: 'linear-gradient(90deg, #FFD700, #DAA520)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>Panel de Admin</h1>
            <p className="text-[#B8860B]">Gestiona LEYBA PRO</p>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4 text-center">
                <Users className="w-6 h-6 text-[#D946EF] mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.total_users}</p>
                <p className="text-sm text-gray-400">Usuarios</p>
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4 text-center">
                <Mic2 className="w-6 h-6 text-[#22C55E] mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.total_artists}</p>
                <p className="text-sm text-gray-400">Artistas</p>
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4 text-center">
                <PlayCircle className="w-6 h-6 text-[#8B5CF6] mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.total_videos}</p>
                <p className="text-sm text-gray-400">Videos</p>
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4 text-center">
                <BarChart3 className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.total_views}</p>
                <p className="text-sm text-gray-400">Vistas</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs - Reorganized for better visibility */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Primary Actions Row */}
          <div className="mb-2">
            <p className="text-xs text-gray-500 mb-1 px-1">📋 Gestión Principal</p>
            <TabsList className="flex flex-wrap gap-1 bg-[#1E1E1E] p-2 rounded-lg h-auto">
              <TabsTrigger value="subscriptions" className={`text-xs px-3 py-2 data-[state=active]:bg-[#D946EF] ${userSubscriptions.length > 0 ? 'ring-2 ring-red-500 bg-red-500/25 animate-pulse font-bold' : ''}`}>
                📝 Inscripciones{userSubscriptions.length > 0 ? ` (${userSubscriptions.length})` : ''}
              </TabsTrigger>
              <TabsTrigger value="screenshots" className={`text-xs px-3 py-2 data-[state=active]:bg-[#22C55E] ${pendingScreenshots.length > 0 ? 'ring-2 ring-red-500 bg-red-500/25 animate-pulse font-bold' : ''}`}>
                📸 Capturas YT{pendingScreenshots.length > 0 ? ` (${pendingScreenshots.length})` : ''}
              </TabsTrigger>
              <TabsTrigger value="task-screenshots" className={`text-xs px-3 py-2 data-[state=active]:bg-[#8B5CF6] ${pendingTaskScreenshots.length > 0 ? 'ring-2 ring-red-500 bg-red-500/25 animate-pulse font-bold' : ''}`}>
                ✅ Tareas{pendingTaskScreenshots.length > 0 ? ` (${pendingTaskScreenshots.length})` : ''}
              </TabsTrigger>
              <TabsTrigger value="daily-visits" className={`text-xs px-3 py-2 data-[state=active]:bg-[#FF6B6B] ${dailyVisitSubmissions.length > 0 ? 'ring-2 ring-red-500 bg-red-500/25 animate-pulse font-bold' : ''}`}>
                👁️ Visitas{dailyVisitSubmissions.length > 0 ? ` (${dailyVisitSubmissions.length})` : ''}
              </TabsTrigger>
              <TabsTrigger value="withdrawals" className={`text-xs px-3 py-2 data-[state=active]:bg-[#F59E0B] ${pendingWithdrawals.length > 0 ? 'ring-2 ring-red-500 bg-red-500/25 animate-pulse font-bold' : ''}`}>
                💸 Retiros{pendingWithdrawals.length > 0 ? ` (${pendingWithdrawals.length})` : ''}
              </TabsTrigger>
              <TabsTrigger value="payments" className="text-xs px-3 py-2 data-[state=active]:bg-[#EC4899]">
                💳 Pagos
              </TabsTrigger>
              <TabsTrigger value="bonuses" className="text-xs px-3 py-2 data-[state=active]:bg-[#06B6D4]">
                🎁 Bonos
              </TabsTrigger>
            </TabsList>
          </div>
          
          {/* Content Management Row */}
          <div className="mb-4">
            <p className="text-xs text-gray-500 mb-1 px-1">⚙️ Contenido y Configuración</p>
            <TabsList className="flex flex-wrap gap-1 bg-[#1E1E1E] p-2 rounded-lg h-auto">
              <TabsTrigger value="users" className="text-xs px-3 py-2 data-[state=active]:bg-[#3B82F6]">
                👥 Usuarios
              </TabsTrigger>
              <TabsTrigger value="locations" className="text-xs px-3 py-2 data-[state=active]:bg-[#14B8A6]">
                📍 Ubicaciones
              </TabsTrigger>
              <TabsTrigger value="artists" className="text-xs px-3 py-2 data-[state=active]:bg-[#D946EF]">
                🎤 Artistas
              </TabsTrigger>
              <TabsTrigger value="radio" className="text-xs px-3 py-2 data-[state=active]:bg-[#FFD700]">
                📻 Radio
              </TabsTrigger>
              <TabsTrigger value="gallery" className="text-xs px-3 py-2 data-[state=active]:bg-[#FFD700]">
                🖼️ Galería
              </TabsTrigger>
              <TabsTrigger value="carousel" className="text-xs px-3 py-2 data-[state=active]:bg-[#F97316]">
                🎠 Carrusel
              </TabsTrigger>
              <TabsTrigger value="accounts" className="text-xs px-3 py-2 data-[state=active]:bg-[#10B981]">
                🏦 Cuentas
              </TabsTrigger>
              <TabsTrigger value="announcements" className="text-xs px-3 py-2 data-[state=active]:bg-[#EF4444]">
                📢 Anuncios
              </TabsTrigger>
              <TabsTrigger value="settings" className="text-xs px-3 py-2 data-[state=active]:bg-[#6B7280]">
                ⚙️ Config
              </TabsTrigger>
              <TabsTrigger value="telegram" className="text-xs px-3 py-2 data-[state=active]:bg-[#0088cc]">
                📱 Telegram ({telegramStats.active_subscribers})
              </TabsTrigger>
              <TabsTrigger value="launches" data-testid="admin-launches-tab" className="text-xs px-3 py-2 data-[state=active]:bg-[#FF6B6B]">
                🚀 Lanzamientos ({allLaunches.length})
              </TabsTrigger>
              <TabsTrigger value="memberships" data-testid="admin-memberships-tab" className={`text-xs px-3 py-2 data-[state=active]:bg-[#22C55E] data-[state=active]:text-black relative ${pendingMemberships.length > 0 ? 'ring-2 ring-red-500 bg-red-500/25 animate-pulse font-bold' : ''}`}>
                💳 Membresías{pendingMemberships.length > 0 ? ` (${pendingMemberships.length})` : ''}
              </TabsTrigger>
              <TabsTrigger value="points-bank" data-testid="admin-points-bank-tab" className="text-xs px-3 py-2 data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">
                🏦 Banco ({pointsBank.total_points?.toLocaleString() || 0} pts)
              </TabsTrigger>
              <TabsTrigger value="artist-links" data-testid="admin-links-tab" className={`text-xs px-3 py-2 data-[state=active]:bg-[#FF0000] ${artistLinks.filter(l => l.status === 'pending').length > 0 ? 'ring-2 ring-red-500 bg-red-500/25 animate-pulse font-bold' : ''}`}>
                🔗 Links{artistLinks.filter(l => l.status === 'pending').length > 0 ? ` (${artistLinks.filter(l => l.status === 'pending').length})` : ''}
              </TabsTrigger>
              <TabsTrigger value="inbox" data-testid="admin-inbox-tab" className={`text-xs px-3 py-2 data-[state=active]:bg-[#22C55E] data-[state=active]:text-black ${inboxSubmissions.filter(s => s.status === 'pending').length > 0 ? 'ring-2 ring-red-500 bg-red-500/25 animate-pulse font-bold' : ''}`}>
                📥 Buzón{inboxSubmissions.filter(s => s.status === 'pending').length > 0 ? ` (${inboxSubmissions.filter(s => s.status === 'pending').length})` : ''}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* User Subscriptions Tab */}
          <TabsContent value="subscriptions" className="mt-6">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-[#D946EF]" />
              <h2 className="text-xl font-bold">Inscripciones Pendientes ({userSubscriptions.length})</h2>
            </div>
            
            {userSubscriptions.length > 0 ? (
              <div className="space-y-3">
                {userSubscriptions.map((sub) => (
                  <Card key={sub.id} className="bg-[#121212] border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className="font-bold text-lg">{sub.user_name}</p>
                          <p className="text-sm text-gray-400">Tel: {sub.user_phone}</p>
                          <p className="text-lg font-bold text-[#22C55E]">RD$ {sub.amount?.toLocaleString()}</p>
                          {sub.reference && (
                            <p className="text-sm text-[#D946EF]">Ref: {sub.reference}</p>
                          )}
                          <p className="text-xs text-gray-500">{new Date(sub.created_at).toLocaleString("es-DO")}</p>
                        </div>
                        {sub.receipt_url && (
                          <button
                            onClick={() => setViewingReceipt(sub.receipt_url)}
                            className="w-20 h-20 rounded-lg border border-white/20 overflow-hidden hover:border-[#D946EF] transition-colors flex-shrink-0"
                          >
                            <img 
                              src={`${BACKEND_URL}${sub.receipt_url}`} 
                              alt="Comprobante" 
                              className="w-full h-full object-cover"
                            />
                          </button>
                        )}
                        <div className="flex flex-col gap-2">
                          <span className={`px-2 py-1 text-xs rounded-full text-center ${
                            sub.status === "confirming" ? "bg-yellow-500 text-black" : "bg-gray-500 text-white"
                          }`}>
                            {sub.status === "confirming" ? "Verificando" : "Pendiente"}
                          </span>
                          {sub.receipt_url && (
                            <Button
                              onClick={() => setViewingReceipt(sub.receipt_url)}
                              size="sm"
                              variant="outline"
                              className="border-[#D946EF] text-[#D946EF]"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Ver
                            </Button>
                          )}
                          <Button
                            onClick={() => handleApproveUserSubscription(sub.id)}
                            size="sm"
                            className="bg-[#22C55E] hover:bg-[#22C55E]/80 text-black"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Aprobar
                          </Button>
                          <Button
                            onClick={() => handleCancelUserSubscription(sub.id, sub.user_name)}
                            data-testid={`cancel-subscription-${sub.id}`}
                            size="sm"
                            variant="outline"
                            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-8 text-center text-gray-400">
                  No hay inscripciones pendientes
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Screenshots Tab */}
          <TabsContent value="screenshots" className="mt-6">
            <div className="flex items-center gap-2 mb-4">
              <Camera className="w-5 h-5 text-[#8B5CF6]" />
              <h2 className="text-xl font-bold">Capturas Pendientes ({pendingScreenshots.length})</h2>
            </div>
            
            {pendingScreenshots.length > 0 ? (
              <div className="space-y-3">
                {pendingScreenshots.map((screenshot) => (
                  <Card key={screenshot.id} className="bg-[#121212] border-white/10">
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        {/* Screenshot Preview */}
                        <div 
                          className="w-32 h-24 rounded-lg overflow-hidden bg-black cursor-pointer border-2 border-white/10 hover:border-[#8B5CF6] transition-colors"
                          onClick={() => setViewingScreenshot(screenshot)}
                        >
                          <img 
                            src={`${BACKEND_URL}${screenshot.screenshot_url}`}
                            alt="Captura"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        
                        {/* Screenshot Info */}
                        <div className="flex-1">
                          <p className="font-bold text-lg">{screenshot.user_name}</p>
                          <p className="text-sm text-gray-400">Tel: {screenshot.user_phone}</p>
                          <div className="mt-1">
                            <p className="text-xs text-gray-400">Campaña: <span className="text-[#FF0000]">{screenshot.campaign_title || 'YouTube'}</span></p>
                            <p className="text-xs text-gray-400">Día: <span className="text-white">{screenshot.view_day}/3</span></p>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{new Date(screenshot.created_at).toLocaleString("es-DO")}</p>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex flex-col gap-2">
                          <Button
                            onClick={() => handleApproveScreenshot(screenshot.id)}
                            className="bg-[#22C55E] hover:bg-[#22C55E]/80 text-black text-sm px-4"
                            size="sm"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Aprobar
                          </Button>
                          <Button
                            onClick={() => handleRejectScreenshot(screenshot.id, "Captura inválida")}
                            variant="outline"
                            className="border-red-500 text-red-400 hover:bg-red-500/20 text-sm px-4"
                            size="sm"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Rechazar
                          </Button>
                          <Button
                            onClick={() => setViewingScreenshot(screenshot)}
                            variant="outline"
                            className="border-white/30 text-sm px-4"
                            size="sm"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Ver
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-8 text-center text-gray-400">
                  No hay capturas pendientes de revisión
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Task Screenshots Tab - Extended with Assign Tasks */}
          <TabsContent value="task-screenshots" className="mt-6 space-y-6">
            {/* Section: Assign New Task */}
            <div>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#22C55E]" />
                  <h2 className="text-xl font-bold">Asignar Tareas a Reclutas</h2>
                </div>
                <div className="flex gap-2">
                  {/* Bulk Assign Dialog */}
                  <Dialog open={showBulkAssignTask} onOpenChange={setShowBulkAssignTask}>
                    <DialogTrigger asChild>
                      <Button className="bg-[#FFD700] hover:bg-[#B8860B] text-black">
                        <Users className="w-4 h-4 mr-2" /> Asignar por Cantidad
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#121212] border-white/10">
                      <DialogHeader>
                        <DialogTitle>Asignar Tarea a Múltiples Usuarios</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleBulkAssignTask} className="space-y-4">
                        <div>
                          <Label>Cantidad de usuarios *</Label>
                          <Input
                            type="number"
                            min="1"
                            max={users.length}
                            value={bulkAssignForm.user_count}
                            onChange={(e) => setBulkAssignForm({ ...bulkAssignForm, user_count: parseInt(e.target.value) || 1 })}
                            className="bg-[#1E1E1E] border-white/10"
                            placeholder="Ej: 30, 75"
                          />
                          <p className="text-xs text-gray-400 mt-1">Máximo disponible: {users.length} usuarios (Pro + Recluta)</p>
                        </div>
                        <div>
                          <Label>Título de la tarea *</Label>
                          <Input
                            value={bulkAssignForm.title}
                            onChange={(e) => setBulkAssignForm({ ...bulkAssignForm, title: e.target.value })}
                            className="bg-[#1E1E1E] border-white/10"
                            placeholder="Ej: Seguir cuenta de Instagram"
                          />
                        </div>
                        <div>
                          <Label>Descripción *</Label>
                          <Textarea
                            value={bulkAssignForm.description}
                            onChange={(e) => setBulkAssignForm({ ...bulkAssignForm, description: e.target.value })}
                            className="bg-[#1E1E1E] border-white/10"
                            placeholder="Describe qué debe hacer el usuario"
                          />
                        </div>
                        <div>
                          <Label>URL de la tarea (opcional)</Label>
                          <Input
                            value={bulkAssignForm.task_url}
                            onChange={(e) => setBulkAssignForm({ ...bulkAssignForm, task_url: e.target.value })}
                            className="bg-[#1E1E1E] border-white/10"
                            placeholder="https://instagram.com/..."
                          />
                        </div>
                        <div>
                          <Label>Recompensa por usuario (RD$)</Label>
                          <Input
                            type="number"
                            value={bulkAssignForm.reward}
                            onChange={(e) => setBulkAssignForm({ ...bulkAssignForm, reward: parseFloat(e.target.value) || 0 })}
                            className="bg-[#1E1E1E] border-white/10"
                          />
                        </div>
                        <div className="p-3 bg-[#1E1E1E] rounded-lg">
                          <p className="text-sm text-gray-400">Total a pagar si todos completan:</p>
                          <p className="text-xl font-bold text-[#FFD700]">RD${(bulkAssignForm.user_count * bulkAssignForm.reward).toLocaleString()}</p>
                        </div>
                        <Button type="submit" className="w-full bg-[#FFD700] hover:bg-[#B8860B] text-black">
                          Asignar a {bulkAssignForm.user_count} Usuarios
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                  
                  {/* Single User Assign Dialog */}
                  <Dialog open={showAssignTask} onOpenChange={setShowAssignTask}>
                    <DialogTrigger asChild>
                      <Button className="bg-[#22C55E] hover:bg-[#22C55E]/80">
                        <Plus className="w-4 h-4 mr-2" /> Tarea Individual
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#121212] border-white/10">
                      <DialogHeader>
                        <DialogTitle>Asignar Tarea a Usuario Recluta</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAssignTask} className="space-y-4">
                        <div>
                          <Label>Usuario Recluta *</Label>
                          <Select
                            value={assignTaskForm.user_id}
                            onValueChange={(v) => setAssignTaskForm({ ...assignTaskForm, user_id: v })}
                          >
                            <SelectTrigger className="bg-[#1E1E1E] border-white/10 cursor-pointer">
                              <SelectValue placeholder="Seleccionar usuario" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1E1E1E] border-white/10">
                              {reclutas.map((user) => (
                                <SelectItem key={user.id} value={user.id} className="cursor-pointer">
                                  {user.name} {user.last_name} - {user.phone}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Título de la tarea *</Label>
                          <Input
                            value={assignTaskForm.title}
                            onChange={(e) => setAssignTaskForm({ ...assignTaskForm, title: e.target.value })}
                            className="bg-[#1E1E1E] border-white/10"
                            placeholder="Ej: Seguir cuenta de Instagram"
                          />
                        </div>
                        <div>
                          <Label>Descripción *</Label>
                          <Textarea
                            value={assignTaskForm.description}
                            onChange={(e) => setAssignTaskForm({ ...assignTaskForm, description: e.target.value })}
                            className="bg-[#1E1E1E] border-white/10"
                            placeholder="Describe qué debe hacer el usuario"
                          />
                        </div>
                        <div>
                          <Label>URL de la tarea (opcional)</Label>
                          <Input
                            value={assignTaskForm.task_url}
                            onChange={(e) => setAssignTaskForm({ ...assignTaskForm, task_url: e.target.value })}
                            className="bg-[#1E1E1E] border-white/10"
                            placeholder="https://instagram.com/..."
                          />
                        </div>
                        <div>
                          <Label>Recompensa (RD$)</Label>
                          <Input
                            type="number"
                            value={assignTaskForm.reward}
                            onChange={(e) => setAssignTaskForm({ ...assignTaskForm, reward: parseFloat(e.target.value) || 0 })}
                            className="bg-[#1E1E1E] border-white/10"
                          />
                        </div>
                        <Button type="submit" className="w-full bg-[#22C55E] hover:bg-[#22C55E]/80">
                          Asignar Tarea
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
              {assignedTasks.length > 0 ? (
                <div className="space-y-3">
                  {assignedTasks.map((task) => (
                    <Card key={task.id} className={`border-white/10 ${
                      task.status === 'completed' ? 'bg-blue-500/10 border-blue-500/50' : 'bg-[#121212]'
                    }`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          {/* Screenshot Preview - PROMINENTE */}
                          <div className="w-24 h-24 rounded-lg overflow-hidden bg-[#1E1E1E] flex-shrink-0 border-2 border-white/20">
                            {task.screenshot_url ? (
                              <img 
                                src={`${BACKEND_URL}${task.screenshot_url}`} 
                                alt="Captura" 
                                className="w-full h-full object-cover cursor-pointer hover:opacity-80"
                                onClick={() => window.open(`${BACKEND_URL}${task.screenshot_url}`, '_blank')}
                              />
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center text-gray-500">
                                <Camera className="w-8 h-8 mb-1" />
                                <span className="text-[10px]">Sin captura</span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-2 py-0.5 text-xs rounded-full font-bold ${
                                task.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                task.status === 'completed' ? 'bg-blue-500 text-white animate-pulse' :
                                task.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                                'bg-red-500/20 text-red-400'
                              }`}>
                                {task.status === 'pending' ? '⏳ Pendiente' :
                                 task.status === 'completed' ? '📸 REVISAR CAPTURA' :
                                 task.status === 'approved' ? '✅ Aprobada' : '❌ Rechazada'}
                              </span>
                            </div>
                            <p className="font-bold">{task.title}</p>
                            <p className="text-sm text-gray-400">{task.description}</p>
                            <p className="text-sm text-[#D946EF] font-bold">👤 {task.user_name} - {task.user_phone}</p>
                            <p className="text-lg font-bold text-[#22C55E]">💰 +RD${task.reward}</p>
                          </div>
                          <div className="flex flex-col gap-2">
                            {task.status === 'completed' && (
                              <>
                                {!task.screenshot_url && (
                                  <div className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded mb-2 text-center">
                                    ⚠️ SIN CAPTURA
                                  </div>
                                )}
                                <Button
                                  size="sm"
                                  className="bg-[#22C55E] hover:bg-[#22C55E]/80"
                                  onClick={() => handleApproveAssignedTask(task.id)}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Aprobar y Pagar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleRejectAssignedTask(task.id)}
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Rechazar
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                              onClick={() => handleDeleteAssignedTask(task.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="p-6 text-center text-gray-400">
                    No hay tareas asignadas. Usa los botones para asignar tareas a usuarios Recluta.
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Section: Pending Task Screenshots */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Camera className="w-5 h-5 text-purple-500" />
                <h2 className="text-xl font-bold">Capturas de Tareas Sociales ({pendingTaskScreenshots.length})</h2>
              </div>
            
              {pendingTaskScreenshots.length > 0 ? (
                <div className="space-y-3">
                  {pendingTaskScreenshots.map((screenshot) => (
                    <Card key={screenshot.id} className="bg-[#121212] border-white/10">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className="w-24 h-24 rounded-lg overflow-hidden bg-[#1E1E1E] flex-shrink-0">
                            <img 
                              src={`${BACKEND_URL}${screenshot.screenshot_url}`} 
                              alt="Captura" 
                              className="w-full h-full object-cover cursor-pointer"
                              onClick={() => window.open(`${BACKEND_URL}${screenshot.screenshot_url}`, '_blank')}
                            />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-2 py-0.5 text-xs rounded-full ${
                                screenshot.type === 'social_task' 
                                  ? 'bg-blue-500/20 text-blue-400' 
                                  : 'bg-pink-500/20 text-pink-400'
                              }`}>
                                {screenshot.type === 'social_task' ? 'Tarea Social' : 'Campaña Compartir'}
                              </span>
                              <span className="px-2 py-0.5 text-xs rounded-full bg-gray-500/20 text-gray-400">
                                {screenshot.task_platform || screenshot.campaign_platform || 'N/A'}
                              </span>
                            </div>
                            <p className="font-bold text-lg">{screenshot.user_name}</p>
                            <p className="text-sm text-gray-400">{screenshot.user_phone}</p>
                            {screenshot.task_username && (
                              <p className="text-sm text-blue-400">@{screenshot.task_username}</p>
                            )}
                            {screenshot.campaign_title && (
                              <p className="text-sm text-pink-400">{screenshot.campaign_title}</p>
                            )}
                            <p className="text-lg font-bold text-[#22C55E]">
                              +RD${screenshot.task_reward || screenshot.share_reward || 0}
                            </p>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button
                              size="sm"
                              className="bg-[#22C55E] hover:bg-[#22C55E]/80"
                              onClick={() => handleApproveTaskScreenshot(screenshot.id)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Aprobar
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleRejectTaskScreenshot(screenshot.id)}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Rechazar
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="p-8 text-center text-gray-400">
                    No hay capturas de tareas sociales pendientes de revisión
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Daily Visits Tab */}
          <TabsContent value="daily-visits" className="mt-6">
            <div className="max-w-2xl mx-auto">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-[#FF6B6B]" />
                  <h2 className="text-xl font-bold">Visitas Diarias</h2>
                </div>
                <Button 
                  onClick={() => setShowAddDailyVisit(true)}
                  className="bg-[#FF6B6B] hover:bg-[#FF6B6B]/80"
                >
                  <Plus className="w-4 h-4 mr-2" /> Nueva Visita
                </Button>
              </div>

            {/* Add Daily Visit Dialog */}
            <Dialog open={showAddDailyVisit} onOpenChange={setShowAddDailyVisit}>
              <DialogContent className="bg-[#1E1E1E] border-white/10">
                <DialogHeader>
                  <DialogTitle>Nueva Visita Diaria</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddDailyVisit} className="space-y-4">
                  <div>
                    <Label>Título</Label>
                    <Input 
                      value={dailyVisitForm.title}
                      onChange={e => setDailyVisitForm({...dailyVisitForm, title: e.target.value})}
                      placeholder="Ej: Visitar canal de YouTube"
                      required
                      className="bg-[#121212] border-white/20"
                    />
                  </div>
                  <div>
                    <Label>Plataforma</Label>
                    <Select value={dailyVisitForm.platform} onValueChange={v => setDailyVisitForm({...dailyVisitForm, platform: v, visit_type: v === "youtube" ? "youtube" : "social"})}>
                      <SelectTrigger className="bg-[#121212] border-white/20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="youtube">YouTube</SelectItem>
                        <SelectItem value="instagram">Instagram</SelectItem>
                        <SelectItem value="tiktok">TikTok</SelectItem>
                        <SelectItem value="facebook">Facebook</SelectItem>
                        <SelectItem value="twitter">Twitter/X</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>URL a visitar</Label>
                    <div className="flex gap-2">
                      <Input 
                        value={dailyVisitForm.target_url}
                        onChange={e => setDailyVisitForm({...dailyVisitForm, target_url: e.target.value})}
                        onPaste={e => {
                          const pastedText = e.clipboardData.getData('text');
                          e.preventDefault();
                          setDailyVisitForm(prev => ({...prev, target_url: pastedText}));
                        }}
                        placeholder="https://..."
                        required
                        autoComplete="off"
                        className="bg-[#121212] border-white/20 flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="border-white/20 text-[#D946EF]"
                        onClick={async () => {
                          try {
                            const text = await navigator.clipboard.readText();
                            setDailyVisitForm(prev => ({...prev, target_url: text}));
                            toast.success("URL pegada");
                          } catch (err) {
                            toast.error("No se pudo acceder al portapapeles");
                          }
                        }}
                      >
                        📋 Pegar
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Recompensa (RD$)</Label>
                      <Input 
                        type="number"
                        value={dailyVisitForm.reward_amount}
                        onChange={e => setDailyVisitForm({...dailyVisitForm, reward_amount: parseFloat(e.target.value)})}
                        min="1"
                        required
                        className="bg-[#121212] border-white/20"
                      />
                    </div>
                    <div>
                      <Label>Cupos diarios</Label>
                      <Input 
                        type="number"
                        value={dailyVisitForm.daily_target}
                        onChange={e => setDailyVisitForm({...dailyVisitForm, daily_target: parseInt(e.target.value)})}
                        min="1"
                        required
                        className="bg-[#121212] border-white/20"
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-[#FF6B6B] hover:bg-[#FF6B6B]/80">
                    Crear Visita
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Screenshot Modal */}
            <Dialog open={!!viewingScreenshot} onOpenChange={() => setViewingScreenshot(null)}>
              <DialogContent className="bg-[#1E1E1E] border-white/10 max-w-4xl max-h-[90vh]">
                <DialogHeader>
                  <DialogTitle>Captura de Pantalla</DialogTitle>
                </DialogHeader>
                {viewingScreenshot && (
                  <div className="flex justify-center">
                    <img 
                      src={typeof viewingScreenshot === 'string' 
                        ? viewingScreenshot 
                        : `${BACKEND_URL}${viewingScreenshot.screenshot_url}`}
                      alt="Captura ampliada"
                      className="max-w-full max-h-[70vh] object-contain rounded-lg"
                    />
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Active Configs */}
            <div className="mb-6">
              <h3 className="text-lg font-bold mb-3">Visitas Activas ({dailyVisitConfigs.length})</h3>
              {dailyVisitConfigs.length === 0 ? (
                <Card className="bg-[#121212] border-white/10 p-6 text-center">
                  <Eye className="w-12 h-12 text-gray-500 mx-auto mb-2" />
                  <p className="text-gray-400">No hay visitas configuradas</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {dailyVisitConfigs.map(config => (
                    <Card key={config.id} className={`border-white/10 ${config.is_active ? 'bg-[#121212]' : 'bg-[#1E1E1E] opacity-60'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                config.platform === 'youtube' ? 'bg-red-500/20 text-red-500' :
                                config.platform === 'instagram' ? 'bg-pink-500/20 text-pink-500' :
                                config.platform === 'tiktok' ? 'bg-cyan-500/20 text-cyan-500' :
                                'bg-blue-500/20 text-blue-500'
                              }`}>
                                {config.platform}
                              </span>
                              {!config.is_active && <span className="text-xs text-gray-500">(Inactiva)</span>}
                            </div>
                            <h4 className="font-bold">{config.title}</h4>
                            <p className="text-xs text-gray-400 truncate">{config.target_url}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm">
                              <span className="text-[#22C55E]">RD${config.reward_amount}</span>
                              <span className="text-gray-400">{config.completions_today}/{config.daily_target} hoy</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleToggleDailyVisit(config.id)}
                              className={config.is_active ? 'border-yellow-500 text-yellow-500' : 'border-green-500 text-green-500'}
                            >
                              {config.is_active ? 'Desactivar' : 'Activar'}
                            </Button>
                            <Button 
                              variant="destructive" 
                              size="sm"
                              onClick={() => handleDeleteDailyVisit(config.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Pending Submissions */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold">Capturas Pendientes ({dailyVisitSubmissions.length})</h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={refreshDailyVisitSubmissions}
                  className="border-[#FF6B6B] text-[#FF6B6B] hover:bg-[#FF6B6B]/10"
                >
                  🔄 Actualizar
                </Button>
              </div>
              <p className="text-xs text-gray-500 mb-3">Se actualiza automáticamente cada 30 segundos</p>
              {dailyVisitSubmissions.length === 0 ? (
                <Card className="bg-[#121212] border-white/10 p-6 text-center">
                  <Camera className="w-12 h-12 text-gray-500 mx-auto mb-2" />
                  <p className="text-gray-400">No hay capturas pendientes</p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {dailyVisitSubmissions.map(submission => (
                    <Card key={submission.id} className="bg-[#121212] border-white/10">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-bold">{submission.user?.name || submission.user_name || 'Usuario'}</p>
                            <p className="text-xs text-gray-400">{submission.user?.phone}</p>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-bold text-[#22C55E]">RD${submission.config?.reward_amount || 0}</span>
                            {submission.time_spent_seconds && (
                              <p className="text-xs text-cyan-400">
                                ⏱️ {Math.floor(submission.time_spent_seconds / 60)}:{(submission.time_spent_seconds % 60).toString().padStart(2, '0')}
                              </p>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-gray-300 mb-3">{submission.config?.title}</p>
                        {submission.screenshot_url && (
                          <div 
                            className="relative cursor-pointer group mb-3"
                            onClick={() => setViewingScreenshot(`${BACKEND_URL}${submission.screenshot_url}`)}
                          >
                            <img 
                              src={`${BACKEND_URL}${submission.screenshot_url}`}
                              alt="Captura"
                              className="w-full h-48 object-cover rounded-lg border-2 border-transparent group-hover:border-[#FF6B6B] transition-all"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <span className="text-white font-bold">🔍 Ver completa</span>
                            </div>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <Button 
                            className="flex-1 bg-green-600 hover:bg-green-700"
                            onClick={() => handleReviewDailyVisit(submission.id, true)}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Aprobar
                          </Button>
                          <Button 
                            variant="destructive"
                            className="flex-1"
                            onClick={() => handleReviewDailyVisit(submission.id, false)}
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Rechazar
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Launch Submissions Section */}
            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  🚀 Capturas de Lanzamientos ({launchSubmissions.length})
                </h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={refreshLaunchSubmissions}
                  className="border-[#FF6B6B] text-[#FF6B6B] hover:bg-[#FF6B6B]/10"
                >
                  🔄 Actualizar
                </Button>
              </div>
              {launchSubmissions.length === 0 ? (
                <Card className="bg-[#121212] border-white/10 p-6 text-center">
                  <Camera className="w-12 h-12 text-gray-500 mx-auto mb-2" />
                  <p className="text-gray-400">No hay capturas de lanzamientos pendientes</p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {launchSubmissions.map(submission => (
                    <Card key={submission.id} className="bg-[#121212] border-[#FF6B6B]/30">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-bold">{submission.user?.name || submission.user_name || 'Usuario'}</p>
                            <p className="text-xs text-gray-400">{submission.user?.phone}</p>
                          </div>
                          <span className="px-2 py-1 bg-[#FF6B6B]/20 text-[#FF6B6B] text-xs rounded font-bold">
                            LANZAMIENTO
                          </span>
                        </div>
                        <p className="text-sm text-[#FF6B6B] font-bold mb-1">{submission.event?.title}</p>
                        <p className="text-xs text-gray-400 mb-3">{submission.event?.artist_name}</p>
                        {submission.screenshot_url && (
                          <div 
                            className="relative cursor-pointer group mb-3"
                            onClick={() => setViewingScreenshot(`${BACKEND_URL}${submission.screenshot_url}`)}
                          >
                            <img 
                              src={`${BACKEND_URL}${submission.screenshot_url}`}
                              alt="Captura"
                              className="w-full h-48 object-cover rounded-lg border-2 border-transparent group-hover:border-[#FF6B6B] transition-all"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <span className="text-white font-bold">🔍 Ver completa</span>
                            </div>
                          </div>
                        )}
                        {/* Reward Amount Input */}
                        <div className="mb-3">
                          <label className="text-xs text-gray-400 mb-1 block">Monto de Recompensa (RD$)</label>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={launchRewardAmounts[submission.id] || ''}
                            onChange={(e) => setLaunchRewardAmounts(prev => ({
                              ...prev,
                              [submission.id]: parseFloat(e.target.value) || 0
                            }))}
                            placeholder="Ej: 30"
                            className="w-full px-3 py-2 bg-[#1E1E1E] border border-white/20 rounded-lg text-white text-lg font-bold"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            className="flex-1 bg-green-600 hover:bg-green-700"
                            onClick={() => handleReviewLaunchSubmission(submission.id, true)}
                            disabled={!launchRewardAmounts[submission.id] || launchRewardAmounts[submission.id] <= 0}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Aprobar
                          </Button>
                          <Button 
                            variant="destructive"
                            className="flex-1"
                            onClick={() => handleReviewLaunchSubmission(submission.id, false)}
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Rechazar
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
            </div>
          </TabsContent>

          {/* Withdrawals Tab */}
          <TabsContent value="withdrawals" className="mt-6">
            <div className="flex items-center gap-2 mb-4">
              <Banknote className="w-5 h-5 text-[#22C55E]" />
              <h2 className="text-xl font-bold">Retiros Pendientes ({pendingWithdrawals.length})</h2>
            </div>
            
            {pendingWithdrawals.length > 0 ? (
              <div className="space-y-3">
                {pendingWithdrawals.map((withdrawal) => (
                  <Card key={withdrawal.id} data-testid={`withdrawal-card-${withdrawal.id}`} className={`bg-[#121212] border-white/10 ${withdrawal.withdrawal_type === 'local' ? 'border-l-4 border-[#D946EF]' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-bold text-lg">{withdrawal.user_name}</p>
                            {withdrawal.withdrawal_type === 'local' && (
                              <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-[#D946EF]/20 text-[#D946EF] border border-[#D946EF]">
                                LOCAL
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-400">
                            Tel: {withdrawal.user_phone}
                          </p>
                          {withdrawal.withdrawal_type !== 'local' && (
                          <div className="mt-2 p-2 bg-[#1E1E1E] rounded-lg">
                            <p className="text-xs text-gray-400">Banco: <span className="text-white">{withdrawal.bank_name}</span></p>
                            <p className="text-xs text-gray-400">Cuenta: <span className="text-white font-mono">{withdrawal.bank_account}</span></p>
                          </div>
                          )}
                          <div className="mt-2 flex items-center gap-4">
                            <div>
                              <p className="text-xs text-gray-400">Solicitado</p>
                              <p className="font-bold">RD${withdrawal.gross_amount}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400">Comisión (10%)</p>
                              <p className="font-bold text-red-400">-RD${withdrawal.commission?.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400">A Pagar</p>
                              <p className="font-bold text-[#22C55E]">RD${withdrawal.net_amount?.toFixed(2)}</p>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{new Date(withdrawal.created_at).toLocaleString("es-DO")}</p>
                        </div>
                        <div className="flex flex-col gap-2">
                          <Button
                            onClick={() => handleProcessWithdrawal(withdrawal.id, "approve")}
                            size="sm"
                            data-testid={`withdrawal-approve-${withdrawal.id}`}
                            className="bg-[#22C55E] hover:bg-[#22C55E]/80 text-black"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Aprobar
                          </Button>
                          <Button
                            onClick={() => handleProcessWithdrawal(withdrawal.id, "reject")}
                            size="sm"
                            data-testid={`withdrawal-reject-${withdrawal.id}`}
                            variant="outline"
                            className="border-red-500 text-red-500 hover:bg-red-500/10"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Rechazar
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-8 text-center text-gray-400">
                  No hay retiros pendientes
                </CardContent>
              </Card>
            )}
            
            {/* Artist Balance Requests */}
            {artistBalanceRequests.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Wallet className="w-5 h-5 text-[#D946EF]" />
                  <h2 className="text-xl font-bold">Recargas de Artistas ({artistBalanceRequests.length})</h2>
                </div>
                <div className="space-y-3">
                  {artistBalanceRequests.map((request) => (
                    <Card key={request.id} className="bg-[#121212] border-white/10">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-bold">{request.artist_name}</p>
                            <p className="text-lg font-bold text-[#D946EF]">
                              {request.currency === "usd" ? "USD" : "RD$"} {request.amount}
                            </p>
                            <p className="text-xs text-gray-500">{new Date(request.created_at).toLocaleString("es-DO")}</p>
                          </div>
                          <Button
                            onClick={() => handleApproveArtistBalance(request.id)}
                            size="sm"
                            className="bg-[#D946EF] hover:bg-[#D946EF]/80"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Aprobar
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Bonuses Tab */}
          <TabsContent value="bonuses" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-[#F59E0B]" />
                <h2 className="text-xl font-bold">Bonos</h2>
              </div>
              <Dialog open={showBonusDialog} onOpenChange={setShowBonusDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black">
                    <Gift className="w-4 h-4 mr-2" />
                    Dar Bono
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#121212] border-white/10">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Gift className="w-5 h-5 text-[#F59E0B]" />
                      Otorgar Bono a Usuario
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleGiveBonus} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Seleccionar Usuario</Label>
                      <select
                        className="w-full bg-[#1E1E1E] border border-white/10 rounded-md p-2 text-white"
                        value={bonusForm.user_id}
                        onChange={(e) => setBonusForm({ ...bonusForm, user_id: e.target.value })}
                        required
                      >
                        <option value="">-- Seleccionar --</option>
                        {users.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.name} {user.last_name} - {user.phone}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Monto (RD$)</Label>
                      <Input
                        type="number"
                        placeholder="Ej: 100"
                        className="bg-[#1E1E1E] border-white/10"
                        value={bonusForm.amount}
                        onChange={(e) => setBonusForm({ ...bonusForm, amount: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Razón del Bono</Label>
                      <Textarea
                        placeholder="Ej: Premio por referidos, compensación, etc."
                        className="bg-[#1E1E1E] border-white/10"
                        value={bonusForm.reason}
                        onChange={(e) => setBonusForm({ ...bonusForm, reason: e.target.value })}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black">
                      Otorgar Bono
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            {/* Recent Bonuses */}
            {bonuses.length > 0 ? (
              <div className="space-y-3">
                {bonuses.slice(0, 20).map((bonus) => (
                  <Card key={bonus.id} className="bg-[#121212] border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold">{bonus.user_name}</p>
                          <p className="text-sm text-gray-400">{bonus.reason}</p>
                          <p className="text-xs text-gray-500">
                            Por: {bonus.admin_name} • {new Date(bonus.created_at).toLocaleString("es-DO")}
                          </p>
                        </div>
                        <p className="text-xl font-bold text-[#F59E0B]">+RD${bonus.amount}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-8 text-center text-gray-400">
                  No hay bonos registrados
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Artist Payments Tab */}
          <TabsContent value="payments" className="mt-6">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5 text-[#22C55E]" />
              <h2 className="text-xl font-bold">Pagos de Artistas Pendientes ({artistPayments.length})</h2>
            </div>
            
            {artistPayments.length > 0 ? (
              <div className="space-y-3">
                {artistPayments.map((payment) => (
                  <Card key={payment.id} className="bg-[#121212] border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-lg">{payment.artist_name}</p>
                          <p className="text-sm text-gray-400">
                            Plan: {payment.plan_name} • {payment.videos} videos
                          </p>
                          <p className="text-lg font-bold text-[#22C55E]">
                            {payment.currency === "rd" ? "RD$" : "USD"} {payment.amount}
                          </p>
                          {payment.reference && (
                            <p className="text-xs text-gray-400">Ref: {payment.reference}</p>
                          )}
                          <p className="text-xs text-gray-500">{new Date(payment.created_at).toLocaleString("es-DO")}</p>
                        </div>
                        <div className="flex flex-col gap-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            payment.status === "confirming" ? "bg-yellow-500 text-black" : "bg-gray-500 text-white"
                          }`}>
                            {payment.status === "confirming" ? "Verificando" : "Pendiente"}
                          </span>
                          <Button
                            onClick={() => handleApproveArtistPayment(payment.id)}
                            size="sm"
                            className="bg-[#22C55E] hover:bg-[#22C55E]/80 text-black"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Aprobar
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-8 text-center text-gray-400">
                  No hay pagos pendientes
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Payment Accounts Tab */}
          <TabsContent value="accounts" className="mt-6">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-5 h-5 text-[#D946EF]" />
              <h2 className="text-xl font-bold">Cuentas para Recibir Pagos</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* RD$ Account */}
              <Card className="bg-[#121212] border-white/10">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="text-[#22C55E] text-2xl font-bold">RD$</span>
                    Pesos Dominicanos
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Banco</Label>
                    <Input
                      className="bg-[#1E1E1E] border-white/10"
                      value={paymentAccounts.rd.bank}
                      onChange={(e) => setPaymentAccounts({
                        ...paymentAccounts,
                        rd: { ...paymentAccounts.rd, bank: e.target.value }
                      })}
                      placeholder="Ej: Banreservas"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Número de Cuenta</Label>
                    <Input
                      className="bg-[#1E1E1E] border-white/10"
                      value={paymentAccounts.rd.account}
                      onChange={(e) => setPaymentAccounts({
                        ...paymentAccounts,
                        rd: { ...paymentAccounts.rd, account: e.target.value }
                      })}
                      placeholder="Ej: 123-456789-0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nombre del Titular</Label>
                    <Input
                      className="bg-[#1E1E1E] border-white/10"
                      value={paymentAccounts.rd.name}
                      onChange={(e) => setPaymentAccounts({
                        ...paymentAccounts,
                        rd: { ...paymentAccounts.rd, name: e.target.value }
                      })}
                      placeholder="Ej: E1 Entertainment SRL"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>RNC / Cédula</Label>
                    <Input
                      className="bg-[#1E1E1E] border-white/10"
                      value={paymentAccounts.rd.rnc}
                      onChange={(e) => setPaymentAccounts({
                        ...paymentAccounts,
                        rd: { ...paymentAccounts.rd, rnc: e.target.value }
                      })}
                      placeholder="Ej: 123456789"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo de Cuenta</Label>
                    <Select
                      value={paymentAccounts.rd.type}
                      onValueChange={(value) => setPaymentAccounts({
                        ...paymentAccounts,
                        rd: { ...paymentAccounts.rd, type: value }
                      })}
                    >
                      <SelectTrigger className="bg-[#1E1E1E] border-white/10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Cuenta Corriente">Cuenta Corriente</SelectItem>
                        <SelectItem value="Cuenta de Ahorros">Cuenta de Ahorros</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* USD Account */}
              <Card className="bg-[#121212] border-white/10">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-6 h-6 text-[#22C55E]" />
                    Dólares USA
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Banco</Label>
                    <Input
                      className="bg-[#1E1E1E] border-white/10"
                      value={paymentAccounts.usd.bank}
                      onChange={(e) => setPaymentAccounts({
                        ...paymentAccounts,
                        usd: { ...paymentAccounts.usd, bank: e.target.value }
                      })}
                      placeholder="Ej: Banreservas (USD)"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Número de Cuenta</Label>
                    <Input
                      className="bg-[#1E1E1E] border-white/10"
                      value={paymentAccounts.usd.account}
                      onChange={(e) => setPaymentAccounts({
                        ...paymentAccounts,
                        usd: { ...paymentAccounts.usd, account: e.target.value }
                      })}
                      placeholder="Ej: 987-654321-0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nombre del Titular</Label>
                    <Input
                      className="bg-[#1E1E1E] border-white/10"
                      value={paymentAccounts.usd.name}
                      onChange={(e) => setPaymentAccounts({
                        ...paymentAccounts,
                        usd: { ...paymentAccounts.usd, name: e.target.value }
                      })}
                      placeholder="Ej: E1 Entertainment SRL"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Código SWIFT</Label>
                    <Input
                      className="bg-[#1E1E1E] border-white/10"
                      value={paymentAccounts.usd.swift}
                      onChange={(e) => setPaymentAccounts({
                        ...paymentAccounts,
                        usd: { ...paymentAccounts.usd, swift: e.target.value }
                      })}
                      placeholder="Ej: BRSVDOSD"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo de Cuenta</Label>
                    <Select
                      value={paymentAccounts.usd.type}
                      onValueChange={(value) => setPaymentAccounts({
                        ...paymentAccounts,
                        usd: { ...paymentAccounts.usd, type: value }
                      })}
                    >
                      <SelectTrigger className="bg-[#1E1E1E] border-white/10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Cuenta Corriente">Cuenta Corriente</SelectItem>
                        <SelectItem value="Cuenta de Ahorros">Cuenta de Ahorros</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Button onClick={handleUpdatePaymentAccounts} className="w-full mt-6 bg-[#22C55E] hover:bg-[#22C55E]/80 text-black h-12 font-bold">
              Guardar Cuentas de Pago
            </Button>
          </TabsContent>

          {/* Announcements Tab */}
          <TabsContent value="announcements" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Anuncios</h2>
              <Dialog open={showAddAnnouncement} onOpenChange={setShowAddAnnouncement}>
                <DialogTrigger asChild>
                  <Button className="bg-[#D946EF] hover:bg-[#D946EF]/80">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#121212] border-white/10">
                  <DialogHeader>
                    <DialogTitle>Crear Anuncio</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddAnnouncement} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Título</Label>
                      <Input className="bg-[#1E1E1E] border-white/10" value={announcementForm.title} onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Contenido</Label>
                      <Textarea className="bg-[#1E1E1E] border-white/10" value={announcementForm.content} onChange={(e) => setAnnouncementForm({ ...announcementForm, content: e.target.value })} required />
                    </div>
                    <div className="space-y-2">
                      <Label>URL de Imagen (Opcional)</Label>
                      <Input className="bg-[#1E1E1E] border-white/10" value={announcementForm.image_url} onChange={(e) => setAnnouncementForm({ ...announcementForm, image_url: e.target.value })} />
                    </div>
                    <Button type="submit" className="w-full bg-[#22C55E] hover:bg-[#22C55E]/80 text-black">Crear</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            <div className="space-y-3">
              {announcements.map((ann) => (
                <Card key={ann.id} className="bg-[#121212] border-white/10">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-[#D946EF]">{ann.title}</h3>
                      <p className="text-sm text-gray-400 line-clamp-1">{ann.content}</p>
                    </div>
                    <Button onClick={() => handleDeleteAnnouncement(ann.id)} variant="ghost" size="sm" className="text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
              {announcements.length === 0 && <p className="text-center text-gray-400 py-8">No hay anuncios</p>}
            </div>
          </TabsContent>

          {/* Social Tasks Tab */}
          <TabsContent value="social" className="mt-6 space-y-6">
            {/* Artist Social Link Submissions */}
            {artistSocialSubmissions.length > 0 && (
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2 mb-4">
                  <Share2 className="w-5 h-5 text-[#D946EF]" />
                  Solicitudes de Redes de Artistas ({artistSocialSubmissions.length})
                </h2>
                <div className="space-y-3">
                  {artistSocialSubmissions.map((submission) => (
                    <Card key={submission.id} className="bg-[#121212] border-purple-500/30">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                              submission.platform === 'instagram' ? 'bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#F77737]' :
                              submission.platform === 'facebook' ? 'bg-[#1877F2]' :
                              submission.platform === 'youtube' ? 'bg-[#FF0000]' :
                              'bg-black'
                            }`}>
                              <span className="text-2xl">
                                {submission.platform === 'instagram' ? '📷' :
                                 submission.platform === 'facebook' ? '👤' :
                                 submission.platform === 'youtube' ? '▶️' : '🎵'}
                              </span>
                            </div>
                            <div>
                              <h4 className="font-bold text-white">@{submission.username}</h4>
                              <p className="text-sm text-[#D946EF]">Artista: {submission.artist_name}</p>
                              <p className="text-xs text-gray-400 capitalize">{submission.platform}</p>
                              {submission.description && (
                                <p className="text-xs text-gray-500 mt-1">{submission.description}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <a 
                              href={submission.profile_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-400 hover:underline"
                            >
                              Ver perfil
                            </a>
                            <Button
                              size="sm"
                              className="bg-[#22C55E] hover:bg-[#22C55E]/80"
                              onClick={() => handleApproveArtistSocial(submission.id)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Aprobar
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleRejectArtistSocial(submission.id)}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Rechazar
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Existing Social Tasks Section */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Tareas Redes Sociales ({socialTasks.length})</h2>
              <Dialog open={showAddSocial} onOpenChange={setShowAddSocial}>
                <DialogTrigger asChild>
                  <Button className="bg-[#E1306C] hover:bg-[#E1306C]/80">
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#121212] border-white/10">
                  <DialogHeader>
                    <DialogTitle>Agregar Tarea</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddSocialTask} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Plataforma</Label>
                      <Select value={socialForm.platform} onValueChange={(value) => setSocialForm({ ...socialForm, platform: value })}>
                        <SelectTrigger className="bg-[#1E1E1E] border-white/10">
                          <SelectValue placeholder="Selecciona" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="instagram">Instagram (+RD$30)</SelectItem>
                          <SelectItem value="facebook">Facebook (+RD$25)</SelectItem>
                          <SelectItem value="tiktok">TikTok (+RD$20)</SelectItem>
                          <SelectItem value="youtube">YouTube (+RD$35)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Usuario</Label>
                      <Input className="bg-[#1E1E1E] border-white/10" placeholder="@usuario" value={socialForm.username} onChange={(e) => setSocialForm({ ...socialForm, username: e.target.value })} required />
                    </div>
                    <div className="space-y-2">
                      <Label>URL del Perfil</Label>
                      <Input className="bg-[#1E1E1E] border-white/10" placeholder="https://..." value={socialForm.profile_url} onChange={(e) => setSocialForm({ ...socialForm, profile_url: e.target.value })} required />
                    </div>
                    <Button type="submit" className="w-full bg-[#22C55E] hover:bg-[#22C55E]/80 text-black">Crear</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            <div className="space-y-3">
              {socialTasks.map((task) => (
                <Card key={task.id} className="bg-[#121212] border-white/10">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getPlatformIcon(task.platform)}
                      <div>
                        <h3 className="font-bold">@{task.username}</h3>
                        <p className="text-sm text-gray-400">{task.platform} - RD${task.reward}</p>
                      </div>
                    </div>
                    <Button onClick={() => handleDeleteSocialTask(task.id)} variant="ghost" size="sm" className="text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
              {socialTasks.length === 0 && <p className="text-center text-gray-400 py-8">No hay tareas</p>}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-6 space-y-6">
            {/* Pending Registrations (new users & artists awaiting admin approval) */}
            {(pendingRegistrations.users.length > 0 || pendingRegistrations.artists.length > 0) && (
              <Card className="bg-[#121212] border-[#FFD700]/40" data-testid="pending-registrations-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#FFD700]">
                    <Clock className="w-5 h-5" />
                    Registros Pendientes de Aprobación ({pendingRegistrations.users.length + pendingRegistrations.artists.length})
                  </CardTitle>
                  <CardDescription>Nuevos registros que necesitan tu aprobación antes de poder iniciar sesión. Rechazar elimina la cuenta.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {pendingRegistrations.users.map(u => (
                    <div key={u.id} data-testid={`pending-reg-user-${u.id}`} className="flex items-center justify-between gap-3 bg-[#0a0a0a] rounded-lg p-3 border border-white/10 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-white truncate">{u.name || 'Usuario'}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#8B5CF6]/20 text-[#8B5CF6]">{u.user_tier === 'pro' ? 'Pro' : 'Recluta'}</span>
                        </div>
                        <p className="text-xs text-gray-400">Tel: {u.phone} · {u.city || 'N/D'}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button onClick={() => handleApproveRegistration('user', u.id, u.name)} data-testid={`approve-reg-user-${u.id}`} size="sm" className="bg-[#22C55E] hover:bg-[#22C55E]/80 text-black">
                          <CheckCircle className="w-4 h-4 mr-1" /> Aprobar
                        </Button>
                        <Button onClick={() => handleRejectRegistration('user', u.id, u.name)} data-testid={`reject-reg-user-${u.id}`} size="sm" variant="outline" className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white">
                          <X className="w-4 h-4 mr-1" /> Rechazar
                        </Button>
                      </div>
                    </div>
                  ))}
                  {pendingRegistrations.artists.map(a => (
                    <div key={a.id} data-testid={`pending-reg-artist-${a.id}`} className="flex items-center justify-between gap-3 bg-[#0a0a0a] rounded-lg p-3 border border-[#D946EF]/20 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-white truncate">{a.name || 'Artista'}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#D946EF]/20 text-[#D946EF]">Artista</span>
                        </div>
                        <p className="text-xs text-gray-400">Tel: {a.phone}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button onClick={() => handleApproveRegistration('artist', a.id, a.name)} data-testid={`approve-reg-artist-${a.id}`} size="sm" className="bg-[#22C55E] hover:bg-[#22C55E]/80 text-black">
                          <CheckCircle className="w-4 h-4 mr-1" /> Aprobar
                        </Button>
                        <Button onClick={() => handleRejectRegistration('artist', a.id, a.name)} data-testid={`reject-reg-artist-${a.id}`} size="sm" variant="outline" className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white">
                          <X className="w-4 h-4 mr-1" /> Rechazar
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Pending Pro Requests Section */}
            {pendingProRequests.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Crown className="w-5 h-5 text-[#FFD700]" />
                  <h2 className="text-xl font-bold">Solicitudes Pro Pendientes ({pendingProRequests.length})</h2>
                </div>
                <div className="space-y-3">
                  {pendingProRequests.map((req) => (
                    <Card key={req.id} className="bg-gradient-to-r from-[#FFD700]/10 to-[#B8860B]/10 border-[#FFD700]/30">
                      <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-bold text-lg">{req.user_name}</h3>
                              <span className={`px-2 py-0.5 text-xs rounded-full ${req.status === 'awaiting_approval' ? 'bg-yellow-500/20 text-yellow-500' : 'bg-blue-500/20 text-blue-400'}`}>
                                {req.status === 'awaiting_approval' ? 'Esperando Aprobación' : 'Sin comprobante'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-400">📱 {req.user_phone}</p>
                            <p className="text-sm text-[#FFD700]">💰 Monto: RD${req.amount}</p>
                            <p className="text-xs text-gray-500">
                              Solicitado: {new Date(req.created_at).toLocaleDateString('es-DO')}
                            </p>
                          </div>
                          {req.receipt_url && (
                            <div className="flex-shrink-0">
                              <img 
                                src={`${BACKEND_URL}${req.receipt_url}`}
                                alt="Comprobante"
                                className="w-32 h-32 object-cover rounded-lg border border-[#FFD700]/30 cursor-pointer hover:opacity-80"
                                onClick={() => window.open(`${BACKEND_URL}${req.receipt_url}`, '_blank')}
                              />
                              <p className="text-xs text-center text-gray-400 mt-1">Ver comprobante</p>
                            </div>
                          )}
                          <div className="flex flex-col gap-2">
                            <Button
                              onClick={() => handleApproveProRequest(req.id)}
                              className="bg-[#FFD700] hover:bg-[#B8860B] text-black"
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Aprobar Pro
                            </Button>
                            <Button
                              onClick={() => handleRejectProRequest(req.id)}
                              data-testid={`cancel-pro-request-${req.id}`}
                              variant="outline"
                              className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                            >
                              <X className="w-4 h-4 mr-2" />
                              Cancelar pedido
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Users List */}
            <div>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 className="text-xl font-bold">Usuarios ({users.length})</h2>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={userFilter === "all" ? "default" : "outline"}
                    onClick={() => setUserFilter("all")}
                    className={userFilter === "all" ? "bg-[#8B5CF6]" : "border-white/20"}
                  >
                    Todos ({users.length})
                  </Button>
                  <Button
                    size="sm"
                    variant={userFilter === "pro" ? "default" : "outline"}
                    onClick={() => setUserFilter("pro")}
                    className={userFilter === "pro" ? "bg-[#FFD700] text-black" : "border-[#FFD700] text-[#FFD700]"}
                  >
                    <Crown className="w-3 h-3 mr-1" />
                    Pro ({users.filter(u => u.user_tier === "pro" || u.pro_status === "approved").length})
                  </Button>
                  <Button
                    size="sm"
                    variant={userFilter === "recluta" ? "default" : "outline"}
                    onClick={() => setUserFilter("recluta")}
                    className={userFilter === "recluta" ? "bg-[#22C55E] text-black" : "border-[#22C55E] text-[#22C55E]"}
                  >
                    Recluta ({users.filter(u => u.user_tier !== "pro" && u.pro_status !== "approved").length})
                  </Button>
                </div>
              </div>
              
              {/* Quick Password Reset Tool */}
              <Card className="bg-gradient-to-r from-[#8B5CF6]/20 to-[#D946EF]/10 border-[#8B5CF6]/50 mb-4">
                <CardContent className="p-4">
                  <h4 className="font-bold text-white mb-3 flex items-center gap-2">
                    <Key className="w-4 h-4 text-[#8B5CF6]" />
                    Resetear Contraseña Rápido
                  </h4>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Teléfono del usuario"
                      className="bg-[#121212] border-white/20 flex-1"
                      id="quick-reset-phone"
                    />
                    <Input
                      placeholder="Nueva contraseña"
                      className="bg-[#121212] border-white/20 flex-1"
                      id="quick-reset-password"
                    />
                    <Button
                      className="bg-[#8B5CF6] hover:bg-[#8B5CF6]/80"
                      onClick={async () => {
                        const phone = document.getElementById('quick-reset-phone').value;
                        const password = document.getElementById('quick-reset-password').value;
                        if (!phone || !password) {
                          toast.error("Ingresa teléfono y contraseña");
                          return;
                        }
                        try {
                          const res = await axios.put(
                            `${API}/admin/users/reset-password-by-phone?phone=${encodeURIComponent(phone)}&new_password=${encodeURIComponent(password)}`,
                            {},
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          toast.success(res.data.message);
                          document.getElementById('quick-reset-phone').value = '';
                          document.getElementById('quick-reset-password').value = '';
                        } catch (error) {
                          toast.error(error.response?.data?.detail || "Error al resetear");
                        }
                      }}
                    >
                      Resetear
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-3">
                {users
                  .filter(u => {
                    if (userFilter === "all") return true;
                    if (userFilter === "pro") return u.user_tier === "pro" || u.pro_status === "approved";
                    if (userFilter === "recluta") return u.user_tier !== "pro" && u.pro_status !== "approved";
                    return true;
                  })
                  .map((u) => (
                  <Card key={u.id} className={`bg-[#121212] border-white/10 ${u.is_blocked ? 'border-red-500/50' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-bold">{u.name} {u.last_name}</h3>
                            {u.user_tier === "pro" || u.pro_status === "approved" ? (
                              <span className="px-2 py-0.5 text-xs bg-gradient-to-r from-[#FFD700] to-[#B8860B] text-black rounded-full font-bold flex items-center gap-1">
                                <Crown className="w-3 h-3" /> PRO
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 text-xs bg-gray-600 text-white rounded-full">Recluta</span>
                            )}
                            {u.is_blocked && (
                              <span className="px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">Bloqueado</span>
                            )}
                            {u.is_subscribed && (
                              <span className="px-2 py-0.5 text-xs bg-[#22C55E] text-black rounded-full">Suscrito</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-400">{u.phone}</p>
                          {u.city && (
                            <p className="text-sm text-[#FFD700]">📍 {u.city}</p>
                          )}
                          {u.is_blocked && u.block_reason && (
                            <p className="text-xs text-red-400 mt-1">Motivo: {u.block_reason}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right mr-2">
                          <p className="text-lg font-bold text-[#22C55E]">RD${u.balance?.toFixed(2)}</p>
                          <p className="text-xs text-gray-400">{u.total_views} vistas</p>
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button
                            onClick={() => {
                              setSelectedUser(u);
                              setShowResetPassword(true);
                            }}
                            size="sm"
                            variant="outline"
                            className="border-[#8B5CF6] text-[#8B5CF6] text-xs"
                          >
                            <Key className="w-3 h-3 mr-1" />
                            Contraseña
                          </Button>
                          {(u.user_tier === "pro" || u.pro_status === "approved" || u.pro_status === "pending") && (
                            <Button
                              onClick={() => handleCancelPro(u.id, `${u.name} ${u.last_name}`)}
                              data-testid={`cancel-pro-${u.id}`}
                              size="sm"
                              variant="outline"
                              className="border-[#FFD700] text-[#FFD700] hover:bg-[#FFD700] hover:text-black text-xs"
                            >
                              <Crown className="w-3 h-3 mr-1" />
                              Cancelar Pro
                            </Button>
                          )}
                          {u.is_blocked ? (
                            <Button
                              onClick={() => handleUnblockUser(u.id, u.name)}
                              size="sm"
                              className="bg-[#22C55E] hover:bg-[#22C55E]/80 text-black text-xs"
                            >
                              <Unlock className="w-3 h-3 mr-1" />
                              Desbloquear
                            </Button>
                          ) : (
                            <Button
                              onClick={() => {
                                setSelectedUser(u);
                                setShowBlockDialog(true);
                              }}
                              size="sm"
                              variant="outline"
                              className="border-red-500 text-red-500 text-xs"
                            >
                              <Ban className="w-3 h-3 mr-1" />
                              Bloquear
                            </Button>
                          )}
                          <Button
                            onClick={() => handleDeleteUser(u.id, `${u.name} ${u.last_name}`)}
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:bg-red-500/20 text-xs"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Eliminar
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              </div>
            </div>
          </TabsContent>

          {/* Locations Tab */}
          <TabsContent value="locations" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#14B8A6]" />
                Usuarios por Ubicación
              </h2>
            </div>
            
            {/* Summary Stats */}
            {locationStats?.summary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <Card className="bg-gradient-to-br from-[#3B82F6]/20 to-[#3B82F6]/5 border-[#3B82F6]/30">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-[#3B82F6]">{locationStats.summary.total_users}</p>
                    <p className="text-xs text-gray-400">Total Usuarios</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-[#FFD700]/20 to-[#FFD700]/5 border-[#FFD700]/30">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-[#FFD700]">{locationStats.summary.total_pro}</p>
                    <p className="text-xs text-gray-400">Usuarios Pro</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-[#22C55E]/20 to-[#22C55E]/5 border-[#22C55E]/30">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-[#22C55E]">{locationStats.summary.total_recluta}</p>
                    <p className="text-xs text-gray-400">Reclutas</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-[#14B8A6]/20 to-[#14B8A6]/5 border-[#14B8A6]/30">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-[#14B8A6]">{locationStats.summary.total_locations}</p>
                    <p className="text-xs text-gray-400">Ubicaciones</p>
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* Locations List */}
            <div className="space-y-3">
              {locationStats?.locations?.length > 0 ? (
                locationStats.locations.map((loc, index) => (
                  <Card key={index} className="bg-[#121212] border-white/10 hover:border-[#14B8A6]/50 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#14B8A6] to-[#0D9488] flex items-center justify-center">
                            <MapPin className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="font-bold text-white">{loc.city || "Sin especificar"}</h3>
                            <p className="text-sm text-gray-400">{loc.country || "País no especificado"}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-[#14B8A6]">{loc.total_users}</p>
                          <p className="text-xs text-gray-400">usuarios</p>
                        </div>
                      </div>
                      
                      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div className="bg-[#1E1E1E] rounded-lg p-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Crown className="w-3 h-3 text-[#FFD700]" />
                            <span className="text-sm font-bold text-[#FFD700]">{loc.pro_users}</span>
                          </div>
                          <p className="text-[10px] text-gray-500">Pro</p>
                        </div>
                        <div className="bg-[#1E1E1E] rounded-lg p-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Users className="w-3 h-3 text-[#22C55E]" />
                            <span className="text-sm font-bold text-[#22C55E]">{loc.recluta_users}</span>
                          </div>
                          <p className="text-[10px] text-gray-500">Reclutas</p>
                        </div>
                        <div className="bg-[#1E1E1E] rounded-lg p-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Wallet className="w-3 h-3 text-[#3B82F6]" />
                            <span className="text-sm font-bold text-[#3B82F6]">RD${loc.total_balance?.toFixed(0) || 0}</span>
                          </div>
                          <p className="text-[10px] text-gray-500">Balance</p>
                        </div>
                        <div className="bg-[#1E1E1E] rounded-lg p-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <DollarSign className="w-3 h-3 text-[#D946EF]" />
                            <span className="text-sm font-bold text-[#D946EF]">RD${loc.total_earnings?.toFixed(0) || 0}</span>
                          </div>
                          <p className="text-[10px] text-gray-500">Ganado</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="p-8 text-center text-gray-400">
                    <MapPin className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No hay datos de ubicación disponibles</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Artists Tab */}
          <TabsContent value="artists" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Mic2 className="w-5 h-5 text-[#D946EF]" />
                Artistas ({allArtists.length})
              </h2>
            </div>
            <div className="space-y-3">
              {allArtists.map((artist) => (
                <Card key={artist.id} className="bg-[#121212] border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {/* Artist Photo or Initial */}
                        {artist.photo_url ? (
                          <img 
                            src={`${BACKEND_URL}${artist.photo_url}`} 
                            alt={artist.name}
                            className="w-14 h-14 rounded-full object-cover border-2 border-[#D946EF]"
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                          />
                        ) : null}
                        <div 
                          className={`w-14 h-14 rounded-full bg-gradient-to-br from-[#D946EF] to-[#8B5CF6] flex items-center justify-center text-white font-bold text-lg ${artist.photo_url ? 'hidden' : ''}`}
                        >
                          {artist.name?.charAt(0) || "A"}
                        </div>
                        <div>
                          <p className="font-bold">{artist.name}</p>
                          <p className="text-sm text-gray-400">{artist.phone}</p>
                          <p className="text-xs text-[#D946EF] font-bold" data-testid={`artists-tab-points-${artist.id}`}>Puntos: {Math.round(artist.balance || 0).toLocaleString()} pts</p>
                          <p className="text-xs text-[#22C55E] font-bold" data-testid={`artists-tab-code-${artist.id}`}>Código: {artist.artist_code || "— sin código —"}</p>
                          {artist.next_launch_datetime ? (
                            <p className="text-xs text-[#FFD700] flex items-center gap-1 mt-1">
                              <Rocket className="w-3 h-3" />
                              {new Date(artist.next_launch_datetime).toLocaleString('es-DO', { dateStyle: 'short', timeStyle: 'short' })}
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500 mt-1">Sin fecha de lanzamiento</p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row items-center gap-2 flex-wrap">
                        {/* Photo Upload/Delete Buttons */}
                        <Button
                          onClick={() => {
                            setSelectedArtist(artist);
                            setArtistPhotoFile(null);
                            setShowArtistPhotoUpload(true);
                          }}
                          size="sm"
                          variant="ghost"
                          className="text-[#22C55E] hover:bg-[#22C55E]/20"
                        >
                          <ImageIcon className="w-4 h-4 mr-1" />
                          Foto
                        </Button>
                        {artist.photo_url && (
                          <Button
                            onClick={() => handleDeleteArtistPhoto(artist)}
                            size="sm"
                            variant="ghost"
                            className="text-orange-500 hover:bg-orange-500/20"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Quitar
                          </Button>
                        )}
                        <Button
                          onClick={() => {
                            setSelectedArtist(artist);
                            setArtistLaunchDateTime(artist.next_launch_datetime ? artist.next_launch_datetime.slice(0, 16) : "");
                            setShowArtistLaunchDate(true);
                          }}
                          size="sm"
                          variant="ghost"
                          className="text-[#D946EF] hover:bg-[#D946EF]/20"
                        >
                          <Rocket className="w-4 h-4 mr-1" />
                          Fecha
                        </Button>
                        <Button
                          onClick={() => {
                            setSelectedArtist(artist);
                            setShowArtistResetPassword(true);
                          }}
                          size="sm"
                          variant="ghost"
                          className="text-[#FFD700] hover:bg-[#FFD700]/20"
                        >
                          <Key className="w-4 h-4 mr-1" />
                          Clave
                        </Button>
                        <Button
                          onClick={() => handleAddPoints(artist.id, artist.name)}
                          data-testid={`artists-tab-add-points-${artist.id}`}
                          size="sm"
                          variant="ghost"
                          className="text-[#D946EF] hover:bg-[#D946EF]/20"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Puntos
                        </Button>
                        <Button
                          onClick={() => handleSetArtistCode(artist.id, artist.name, artist.artist_code)}
                          data-testid={`artists-tab-code-btn-${artist.id}`}
                          size="sm"
                          variant="ghost"
                          className="text-[#22C55E] hover:bg-[#22C55E]/20"
                        >
                          <Key className="w-4 h-4 mr-1" />
                          Código
                        </Button>
                        <Button
                          onClick={() => handleDeleteArtist(artist.id, artist.name)}
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:bg-red-500/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {allArtists.length === 0 && (
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="p-8 text-center text-gray-400">
                    No hay artistas registrados
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Settings Tab */}
          {/* Radio Tab */}
          <TabsContent value="radio" className="mt-6 space-y-6">
            {/* Pending Radio Submissions from Artists */}
            {radioSubmissions.length > 0 && (
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2 mb-4">
                  <Music className="w-5 h-5 text-[#FFD700]" />
                  Solicitudes de Radio Pendientes ({radioSubmissions.length})
                </h2>
                <div className="space-y-3">
                  {radioSubmissions.map((submission) => (
                    <Card key={submission.id} className="bg-[#121212] border-[#FFD700]/30">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          {/* Payment proof thumbnail */}
                          {submission.payment_proof_url && (
                            <div className="w-20 h-20 rounded-lg overflow-hidden bg-[#1E1E1E] flex-shrink-0">
                              <img 
                                src={`${BACKEND_URL}${submission.payment_proof_url}`} 
                                alt="Comprobante" 
                                className="w-full h-full object-cover cursor-pointer"
                                onClick={() => window.open(`${BACKEND_URL}${submission.payment_proof_url}`, '_blank')}
                              />
                            </div>
                          )}
                          <div className="flex-1">
                            <h4 className="font-bold text-lg text-white">{submission.title}</h4>
                            <p className="text-sm text-[#FFD700]">Artista: {submission.artist_name}</p>
                            <p className="text-sm text-gray-400">Tel: {submission.artist_phone}</p>
                            <p className="text-sm text-green-400 font-bold">Pago: RD$1,100</p>
                            
                            {/* Audio player */}
                            <div className="mt-2">
                              <audio controls className="w-full h-8" preload="metadata">
                                <source src={`${BACKEND_URL}${submission.mp3_url}`} type="audio/mpeg" />
                              </audio>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button
                              size="sm"
                              className="bg-[#22C55E] hover:bg-[#22C55E]/80"
                              onClick={() => handleApproveRadioSubmission(submission.id)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Aprobar
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleRejectRadioSubmission(submission.id)}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Rechazar
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Existing Radio Tracks */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Music className="w-5 h-5 text-[#D946EF]" />
                  Radio LEYBA ({radioTracks.length} canciones)
                </h2>
                <Dialog open={showAddRadio} onOpenChange={setShowAddRadio}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#D946EF] hover:bg-[#D946EF]/80">
                      <Plus className="w-4 h-4 mr-2" />
                      Subir MP3
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#121212] border-white/10 max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Music className="w-5 h-5 text-[#D946EF]" />
                        Subir Canción a la Radio
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddRadioTrack} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Archivo MP3 *</Label>
                        <Input
                          type="file"
                          accept=".mp3,audio/mpeg"
                          className="bg-[#1E1E1E] border-white/10"
                          onChange={(e) => setRadioFile(e.target.files[0])}
                        />
                        <p className="text-xs text-gray-400">o proporciona una URL abajo</p>
                      </div>
                      <div className="space-y-2">
                        <Label>URL del MP3 (opcional)</Label>
                        <Input
                          placeholder="https://..."
                          className="bg-[#1E1E1E] border-white/10"
                          value={radioForm.mp3_url}
                          onChange={(e) => setRadioForm({ ...radioForm, mp3_url: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Título *</Label>
                        <Input
                          placeholder="Nombre de la canción"
                          className="bg-[#1E1E1E] border-white/10"
                          value={radioForm.title}
                          onChange={(e) => setRadioForm({ ...radioForm, title: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Artista *</Label>
                      <Input
                        placeholder="Nombre del artista"
                        className="bg-[#1E1E1E] border-white/10"
                        value={radioForm.artist_name}
                        onChange={(e) => setRadioForm({ ...radioForm, artist_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Reproducciones por día *</Label>
                      <Input
                        type="number"
                        min="1"
                        placeholder="10"
                        className="bg-[#1E1E1E] border-white/10"
                        value={radioForm.daily_plays}
                        onChange={(e) => setRadioForm({ ...radioForm, daily_plays: parseInt(e.target.value) || 10 })}
                        required
                      />
                      <p className="text-xs text-gray-400">Cuántas veces sonará esta canción por día</p>
                    </div>
                    <Button type="submit" className="w-full bg-[#D946EF] hover:bg-[#D946EF]/80">
                      Subir Canción
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            {radioTracks.length > 0 ? (
              <div className="space-y-3">
                {radioTracks.map((track) => (
                  <Card key={track.id} className="bg-[#121212] border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#D946EF] to-[#8B5CF6] flex items-center justify-center">
                            <Music className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="font-bold">{track.title}</p>
                            <p className="text-sm text-gray-400">{track.artist_name}</p>
                            <p className="text-xs text-[#22C55E]">
                              {track.plays_today || 0}/{track.daily_plays || 10} reproducciones hoy
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              min="1"
                              className="w-16 h-8 bg-[#1E1E1E] border-white/10 text-center text-sm"
                              defaultValue={track.daily_plays || 10}
                              onBlur={(e) => {
                                const newValue = parseInt(e.target.value);
                                if (newValue && newValue !== track.daily_plays) {
                                  handleUpdateDailyPlays(track.id, newValue);
                                }
                              }}
                            />
                            <span className="text-xs text-gray-400">/día</span>
                          </div>
                          <Button
                            onClick={() => handleDeleteRadioTrack(track.id)}
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:bg-red-500/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-8 text-center text-gray-400">
                  <Music className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No hay canciones en la radio</p>
                  <p className="text-sm">Sube tu primer MP3 para comenzar</p>
                </CardContent>
              </Card>
            )}
            </div>
          </TabsContent>

          {/* Gallery Tab */}
          <TabsContent value="gallery" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-[#FFD700]" />
                Galería de Artistas ({galleryArtworks.length})
              </h2>
              <Dialog open={showAddGallery} onOpenChange={setShowAddGallery}>
                <DialogTrigger asChild>
                  <Button className="bg-[#FFD700] hover:bg-[#DAA520] text-black">
                    <Plus className="w-4 h-4 mr-2" />
                    Subir Imagen
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#121212] border-white/10 max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <ImageIcon className="w-5 h-5 text-[#FFD700]" />
                      Subir Imagen de Artista
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleUploadGalleryArtwork} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Imagen *</Label>
                      <Input
                        type="file"
                        accept="image/*"
                        className="bg-[#1E1E1E] border-white/10"
                        onChange={(e) => setGalleryFile(e.target.files[0])}
                        required
                      />
                      <p className="text-xs text-gray-400">JPG, PNG, WebP o GIF</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Título *</Label>
                      <Input
                        placeholder="Nombre del arte"
                        className="bg-[#1E1E1E] border-white/10"
                        value={galleryForm.title}
                        onChange={(e) => setGalleryForm({ ...galleryForm, title: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nombre del Artista</Label>
                      <Input
                        placeholder="Artista (opcional)"
                        className="bg-[#1E1E1E] border-white/10"
                        value={galleryForm.artist_name}
                        onChange={(e) => setGalleryForm({ ...galleryForm, artist_name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Descripción</Label>
                      <Textarea
                        placeholder="Descripción de la imagen (opcional)"
                        className="bg-[#1E1E1E] border-white/10"
                        value={galleryForm.description}
                        onChange={(e) => setGalleryForm({ ...galleryForm, description: e.target.value })}
                      />
                    </div>
                    <Button 
                      type="submit" 
                      disabled={uploadingGallery}
                      className="w-full bg-[#FFD700] hover:bg-[#DAA520] text-black"
                    >
                      {uploadingGallery ? "Subiendo..." : "Subir Imagen"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {galleryArtworks.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {galleryArtworks.map((artwork) => (
                  <Card key={artwork.id} className={`bg-[#121212] border-white/10 overflow-hidden ${!artwork.is_active ? 'opacity-50' : ''}`}>
                    <div className="relative aspect-square">
                      <img 
                        src={`${BACKEND_URL}${artwork.image_url}`}
                        alt={artwork.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <CardContent className="p-3">
                      <h4 className="font-bold text-sm truncate">{artwork.title}</h4>
                      {artwork.artist_name && (
                        <p className="text-xs text-[#FFD700] truncate">{artwork.artist_name}</p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className={artwork.is_active ? "text-green-500" : "text-gray-500"}
                          onClick={() => handleToggleGalleryArtwork(artwork.id, artwork.is_active)}
                        >
                          {artwork.is_active ? <Eye className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:bg-red-500/20"
                          onClick={() => handleDeleteGalleryArtwork(artwork.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-8 text-center text-gray-400">
                  <ImageIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No hay arte en la galería</p>
                  <p className="text-sm">Sube imágenes de artistas a la galería</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Carousel Tab */}
          <TabsContent value="carousel" className="mt-6">
            {/* Quick Artist Photo Upload Section */}
            <Card className="bg-gradient-to-r from-[#D946EF]/20 to-[#8B5CF6]/10 border-[#D946EF]/50 mb-6">
              <CardContent className="p-5">
                <div className="flex flex-col md:flex-row items-center gap-4">
                  <div className="flex-1">
                    <h3 className="font-bold text-white text-lg flex items-center gap-2 mb-2">
                      <Camera className="w-5 h-5 text-[#D946EF]" />
                      Subir Foto de Artista al Carrusel
                    </h3>
                    <p className="text-sm text-gray-400">Sube las fotos o afiches que te mandan los artistas directamente desde tu galería</p>
                  </div>
                  <div className="flex flex-col gap-2 w-full md:w-auto">
                    <Input
                      id="quick-artist-name"
                      placeholder="Nombre del artista"
                      className="bg-[#121212] border-white/20"
                    />
                    <label className="cursor-pointer w-full">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/jpg"
                        className="hidden"
                        id="quick-carousel-photo"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          const artistName = document.getElementById('quick-artist-name').value;
                          if (!file) return;
                          if (!artistName) {
                            toast.error("Escribe el nombre del artista primero");
                            return;
                          }
                          try {
                            toast.info("Subiendo foto...");
                            // First upload the image
                            const formData = new FormData();
                            formData.append("file", file);
                            const uploadRes = await axios.post(
                              `${API}/admin/carousel/upload-image`,
                              formData,
                              { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
                            );
                            
                            // Then create the slide
                            const slideData = {
                              title: artistName,
                              subtitle: "Artista LEYBA PRO",
                              description: `Próximamente nuevo lanzamiento de ${artistName}`,
                              slide_type: "artist",
                              bg_gradient: "from-[#D946EF] to-[#8B5CF6]",
                              icon: "music",
                              youtube_url: "",
                              image_url: uploadRes.data.image_url,
                              artist_id: null,
                              launch_datetime: null
                            };
                            await axios.post(`${API}/admin/carousel/slides`, slideData, { headers: { Authorization: `Bearer ${token}` } });
                            toast.success(`¡Foto de ${artistName} agregada al carrusel!`);
                            document.getElementById('quick-artist-name').value = '';
                            e.target.value = '';
                            fetchAllData();
                          } catch (error) {
                            toast.error("Error al subir foto");
                          }
                        }}
                      />
                      <Button className="w-full bg-[#D946EF] hover:bg-[#D946EF]/80 text-white font-bold" asChild>
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          Seleccionar Foto de Galería
                        </span>
                      </Button>
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Layers className="w-5 h-5 text-[#F59E0B]" />
                Carrusel del Inicio ({carouselSlides.length} slides)
              </h2>
              <Dialog open={showAddCarousel} onOpenChange={setShowAddCarousel}>
                <DialogTrigger asChild>
                  <Button className="bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black">
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Slide
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#121212] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Layers className="w-5 h-5 text-[#F59E0B]" />
                      Nuevo Slide del Carrusel
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddCarouselSlide} className="space-y-4">
                    {/* Slide Type Selection */}
                    <div className="space-y-2">
                      <Label>Tipo de Slide *</Label>
                      <Select 
                        value={carouselForm.slide_type} 
                        onValueChange={(v) => setCarouselForm({ ...carouselForm, slide_type: v, artist_id: "" })}
                      >
                        <SelectTrigger className="bg-[#1E1E1E] border-white/10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="promo">🎯 Promocional</SelectItem>
                          <SelectItem value="artist">🎤 Artista</SelectItem>
                          <SelectItem value="video">📺 Video</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Artist Selector - Only shown for artist type */}
                    {carouselForm.slide_type === "artist" && (
                      <>
                        <div className="space-y-2">
                          <Label>Seleccionar Artista *</Label>
                          <Select 
                            value={carouselForm.artist_id} 
                            onValueChange={(v) => {
                              const artist = allArtists.find(a => a.id === v);
                              if (artist) {
                                setCarouselForm({ 
                                  ...carouselForm, 
                                  artist_id: v,
                                  title: artist.name,
                                  subtitle: artist.bio || "Artista LEYBA PRO",
                                  description: `Próximamente nuevo lanzamiento de ${artist.name}`,
                                  image_url: artist.photo_url || ""
                                });
                              }
                            }}
                          >
                            <SelectTrigger className="bg-[#1E1E1E] border-white/10">
                              <SelectValue placeholder="Selecciona un artista..." />
                            </SelectTrigger>
                            <SelectContent>
                              {allArtists.map(artist => (
                                <SelectItem key={artist.id} value={artist.id}>
                                  {artist.name} {artist.photo_url ? "📷" : ""}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Foto/Afiche del Artista (opcional)</Label>
                          <Input 
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/jpg"
                            className="bg-[#1E1E1E] border-white/10" 
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                // Store file for upload
                                setCarouselForm({ ...carouselForm, photoFile: file });
                              }
                            }}
                          />
                          <p className="text-xs text-gray-500">Si no subes foto, se usará la foto del artista seleccionado</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Fecha de Lanzamiento (opcional)</Label>
                          <Input 
                            type="datetime-local"
                            className="bg-[#1E1E1E] border-white/10" 
                            value={carouselForm.launch_datetime} 
                            onChange={(e) => setCarouselForm({ ...carouselForm, launch_datetime: e.target.value })} 
                          />
                          <p className="text-xs text-gray-500">Si no hay fecha, se mostrará "PRONTO"</p>
                        </div>
                      </>
                    )}

                    <div className="space-y-2">
                      <Label>Título *</Label>
                      <Input 
                        placeholder="Ej: 🚀 Lanza Tu Video" 
                        className="bg-[#1E1E1E] border-white/10" 
                        value={carouselForm.title} 
                        onChange={(e) => setCarouselForm({ ...carouselForm, title: e.target.value })} 
                        required 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Subtítulo *</Label>
                      <Input 
                        placeholder="Ej: Impulsa tu carrera musical" 
                        className="bg-[#1E1E1E] border-white/10" 
                        value={carouselForm.subtitle} 
                        onChange={(e) => setCarouselForm({ ...carouselForm, subtitle: e.target.value })} 
                        required 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Descripción *</Label>
                      <Textarea 
                        placeholder="Descripción del servicio..." 
                        className="bg-[#1E1E1E] border-white/10" 
                        value={carouselForm.description} 
                        onChange={(e) => setCarouselForm({ ...carouselForm, description: e.target.value })} 
                        required 
                      />
                    </div>

                    {/* Only show icon and gradient for non-artist slides */}
                    {carouselForm.slide_type !== "artist" && (
                      <>
                        <div className="space-y-2">
                          <Label>Icono</Label>
                          <Select value={carouselForm.icon} onValueChange={(v) => setCarouselForm({ ...carouselForm, icon: v })}>
                            <SelectTrigger className="bg-[#1E1E1E] border-white/10">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ICON_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Color de Fondo</Label>
                          <Select value={carouselForm.bg_gradient} onValueChange={(v) => setCarouselForm({ ...carouselForm, bg_gradient: v })}>
                            <SelectTrigger className="bg-[#1E1E1E] border-white/10">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {GRADIENT_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}

                    {carouselForm.slide_type === "video" && (
                      <div className="space-y-2">
                        <Label>URL de YouTube (opcional)</Label>
                        <Input 
                          placeholder="https://youtube.com/watch?v=..." 
                          className="bg-[#1E1E1E] border-white/10" 
                          value={carouselForm.youtube_url} 
                          onChange={(e) => setCarouselForm({ ...carouselForm, youtube_url: e.target.value })} 
                        />
                      </div>
                    )}

                    <Button type="submit" className="w-full bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black font-bold">
                      Agregar al Carrusel
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {carouselSlides.length > 0 ? (
              <div className="space-y-3">
                {carouselSlides.map((slide, index) => (
                  <Card key={slide.id} className={`border-white/10 ${slide.is_active ? 'bg-[#121212]' : 'bg-[#121212]/50 opacity-60'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {/* Show artist photo or gradient icon */}
                          {slide.slide_type === 'artist' && slide.image_url ? (
                            <img 
                              src={`${BACKEND_URL}${slide.image_url}`} 
                              alt={slide.title}
                              className="w-16 h-16 rounded-lg object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div className={`w-16 h-16 rounded-lg bg-gradient-to-br ${slide.bg_gradient} flex items-center justify-center`}>
                              {slide.slide_type === 'artist' && <Users className="w-8 h-8 text-white" />}
                              {slide.icon === 'rocket' && slide.slide_type !== 'artist' && <Rocket className="w-8 h-8 text-white" />}
                              {slide.icon === 'youtube' && <Youtube className="w-8 h-8 text-white" />}
                              {slide.icon === 'share' && <Share2 className="w-8 h-8 text-white" />}
                              {slide.icon === 'star' && <Star className="w-8 h-8 text-white" />}
                              {slide.icon === 'music' && <Music className="w-8 h-8 text-white" />}
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-bold text-white">{slide.title}</h4>
                              {slide.slide_type === 'artist' && (
                                <span className="text-xs bg-[#D946EF]/20 text-[#D946EF] px-2 py-0.5 rounded-full">Artista</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-400">{slide.subtitle}</p>
                            <p className="text-xs text-gray-500 line-clamp-1">{slide.description}</p>
                            {slide.launch_datetime && (
                              <p className="text-xs text-[#FFD700] flex items-center gap-1 mt-1">
                                <Clock className="w-3 h-3" />
                                {new Date(slide.launch_datetime).toLocaleString('es-DO', { dateStyle: 'short', timeStyle: 'short' })}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="flex flex-col mr-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              data-testid={`carousel-move-up-${slide.id}`}
                              disabled={index === 0}
                              onClick={() => handleMoveSlide(index, -1)}
                              className="h-5 w-6 text-gray-300 hover:text-[#D946EF] hover:bg-[#D946EF]/10 disabled:opacity-20"
                              title="Mover arriba"
                            >
                              <ChevronUp className="w-4 h-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              data-testid={`carousel-move-down-${slide.id}`}
                              disabled={index === carouselSlides.length - 1}
                              onClick={() => handleMoveSlide(index, 1)}
                              className="h-5 w-6 text-gray-300 hover:text-[#D946EF] hover:bg-[#D946EF]/10 disabled:opacity-20"
                              title="Mover abajo"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </Button>
                          </div>
                          <span className="text-xs text-gray-500 mr-2">#{index + 1}</span>
                          {/* Photo upload for artist slides */}
                          {slide.slide_type === 'artist' && (
                            <>
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept="image/jpeg,image/png,image/webp"
                                  className="hidden"
                                  onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    try {
                                      const formData = new FormData();
                                      formData.append("file", file);
                                      const uploadRes = await axios.post(
                                        `${API}/admin/carousel/upload-image`,
                                        formData,
                                        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
                                      );
                                      // Update slide with new image
                                      await axios.put(
                                        `${API}/admin/carousel/slides/${slide.id}/image`,
                                        { image_url: uploadRes.data.image_url },
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      toast.success("Foto actualizada");
                                      fetchAllData();
                                    } catch (error) {
                                      toast.error("Error al subir foto");
                                    }
                                  }}
                                />
                                <Button size="sm" variant="ghost" className="text-[#22C55E] hover:bg-[#22C55E]/20" asChild>
                                  <span><ImageIcon className="w-4 h-4 mr-1" />Foto</span>
                                </Button>
                              </label>
                              {slide.image_url && (
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  className="text-orange-500 hover:bg-orange-500/20"
                                  onClick={async () => {
                                    if (!window.confirm("¿Quitar la foto de este slide?")) return;
                                    try {
                                      await axios.put(
                                        `${API}/admin/carousel/slides/${slide.id}/image`,
                                        { image_url: "" },
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      toast.success("Foto eliminada");
                                      fetchAllData();
                                    } catch (error) {
                                      toast.error("Error al eliminar foto");
                                    }
                                  }}
                                >
                                  <X className="w-4 h-4 mr-1" />Quitar
                                </Button>
                              )}
                              {/* Date/Time button for artist slides */}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-[#FFD700] hover:bg-[#FFD700]/20"
                                onClick={() => {
                                  setSelectedSlideId(slide.id);
                                  setSlideDatetime(slide.launch_datetime ? slide.launch_datetime.slice(0, 16) : "");
                                  setShowSlideDatetime(true);
                                }}
                              >
                                <Clock className="w-4 h-4 mr-1" />Fecha
                              </Button>
                            </>
                          )}
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className={`border-white/20 ${slide.is_active ? 'text-green-400' : 'text-gray-400'}`}
                            onClick={() => handleToggleCarouselSlide(slide.id)}
                          >
                            {slide.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive" 
                            onClick={() => handleDeleteCarouselSlide(slide.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-8 text-center text-gray-400">
                  <Layers className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No hay slides en el carrusel</p>
                  <p className="text-sm">Agrega tu primer slide para promocionar servicios</p>
                </CardContent>
              </Card>
            )}

            {/* Dialog for editing slide datetime */}
            <Dialog open={showSlideDatetime} onOpenChange={setShowSlideDatetime}>
              <DialogContent className="bg-[#121212] border-white/10">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#FFD700]" />
                    Fecha y Hora del Lanzamiento
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Fecha y Hora</Label>
                    <Input
                      type="datetime-local"
                      className="bg-[#1E1E1E] border-white/10"
                      value={slideDatetime}
                      onChange={(e) => setSlideDatetime(e.target.value)}
                    />
                    <p className="text-xs text-gray-500">Si no hay fecha, se mostrará "PRONTO" en el carrusel</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleUpdateSlideDatetime}
                      className="flex-1 bg-[#FFD700] hover:bg-[#FFD700]/80 text-black"
                    >
                      {slideDatetime ? "Guardar Fecha" : "Quitar Fecha"}
                    </Button>
                    {slideDatetime && (
                      <Button 
                        variant="outline"
                        className="border-red-500/50 text-red-500 hover:bg-red-500/20"
                        onClick={() => setSlideDatetime("")}
                      >
                        Limpiar
                      </Button>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="settings" className="mt-6 space-y-6">
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle>WhatsApp</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Número</Label>
                  <Input className="bg-[#1E1E1E] border-white/10" value={whatsappConfig.number} onChange={(e) => setWhatsappConfig({ ...whatsappConfig, number: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Mensaje</Label>
                  <Textarea className="bg-[#1E1E1E] border-white/10" value={whatsappConfig.message} onChange={(e) => setWhatsappConfig({ ...whatsappConfig, message: e.target.value })} />
                </div>
                <Button onClick={handleUpdateWhatsapp} className="w-full bg-[#25D366] hover:bg-[#25D366]/80">Guardar</Button>
              </CardContent>
            </Card>

            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle>Agregar Administrador</CardTitle>
              </CardHeader>
              <CardContent>
                <Dialog open={showAddAdmin} onOpenChange={setShowAddAdmin}>
                  <DialogTrigger asChild>
                    <Button className="w-full bg-[#8B5CF6] hover:bg-[#8B5CF6]/80">
                      <Plus className="w-4 h-4 mr-2" />
                      Nuevo Admin
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#121212] border-white/10">
                    <DialogHeader>
                      <DialogTitle>Crear Admin</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddAdmin} className="space-y-4">
                      <Input className="bg-[#1E1E1E] border-white/10" placeholder="Teléfono" value={adminForm.phone} onChange={(e) => setAdminForm({ ...adminForm, phone: e.target.value })} required />
                      <Input className="bg-[#1E1E1E] border-white/10" placeholder="Nombre" value={adminForm.name} onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })} required />
                      <Input className="bg-[#1E1E1E] border-white/10" type="password" placeholder="Contraseña" value={adminForm.password} onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })} required />
                      <Button type="submit" className="w-full bg-[#22C55E] hover:bg-[#22C55E]/80 text-black">Crear</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Telegram Tab */}
          <TabsContent value="telegram" className="mt-6 space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <svg className="w-6 h-6 text-[#0088cc]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
              </svg>
              <h2 className="text-xl font-bold">Alertas por Telegram</h2>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-[#0088cc]/10 border-[#0088cc]/30">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-[#0088cc]">{telegramStats.active_subscribers}</p>
                  <p className="text-sm text-gray-400">Suscriptores Activos</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-500/10 border-gray-500/30">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-gray-400">{telegramStats.total_subscribers - telegramStats.active_subscribers}</p>
                  <p className="text-sm text-gray-400">Inactivos</p>
                </CardContent>
              </Card>
            </div>

            {/* Bot Info */}
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle className="text-lg">Bot de Telegram</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-[#1E1E1E] p-4 rounded-lg">
                  <p className="text-sm text-gray-400 mb-2">Enlace del bot:</p>
                  <a 
                    href="https://t.me/Leybapro_bot" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#0088cc] font-bold hover:underline"
                  >
                    @Leybapro_bot
                  </a>
                </div>

                <div className="bg-[#1E1E1E] p-4 rounded-lg">
                  <p className="text-sm text-gray-400 mb-2">Cómo funciona:</p>
                  <ul className="text-sm space-y-1">
                    <li>• Los usuarios abren Telegram y buscan @Leybapro_bot</li>
                    <li>• Envían <code className="bg-gray-800 px-1 rounded">/start</code> para suscribirse</li>
                    <li>• Cuando hay un lanzamiento, reciben alerta automática</li>
                    <li>• Funciona <b className="text-green-400">aunque la app esté cerrada</b></li>
                  </ul>
                </div>

                <Button
                  onClick={handleSendTelegramTestAlert}
                  disabled={sendingTelegramAlert || telegramStats.active_subscribers === 0}
                  className="w-full bg-[#0088cc] hover:bg-[#0088cc]/80 text-white"
                >
                  {sendingTelegramAlert ? (
                    "Enviando..."
                  ) : (
                    <>
                      🔔 Enviar Alerta de Prueba ({telegramStats.active_subscribers} suscriptores)
                    </>
                  )}
                </Button>
                {telegramStats.active_subscribers === 0 && (
                  <p className="text-xs text-yellow-500 text-center">
                    ⚠️ No hay suscriptores activos. Pídele a los usuarios que envíen /start al bot.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Launches Tab */}
          <TabsContent value="launches" className="mt-6 space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <Rocket className="w-6 h-6 text-[#FF6B6B]" />
              <h2 className="text-xl font-bold">Gestión de Lanzamientos ({allLaunches.length})</h2>
            </div>

            {allLaunches.length === 0 ? (
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-8 text-center text-gray-400">
                  <Rocket className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No hay lanzamientos registrados</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {allLaunches.map(launch => {
                  const launchDate = new Date(launch.launch_datetime);
                  const now = new Date();
                  const isPast = launchDate < now;
                  const isActive = !isPast && (now - launchDate) / 1000 < 720; // 12 min window
                  
                  return (
                    <Card key={launch.id} data-testid={`admin-launch-card-${launch.id}`} className={`border ${isPast ? 'border-gray-600 bg-gray-800/30' : isActive ? 'border-green-500 bg-green-500/10' : 'border-yellow-500 bg-yellow-500/10'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-bold">{launch.title}</h3>
                              {isPast && <span className="text-xs bg-gray-600 px-2 py-0.5 rounded">TERMINADO</span>}
                              {isActive && <span className="text-xs bg-green-500 text-black px-2 py-0.5 rounded animate-pulse">EN CURSO</span>}
                              {!isPast && !isActive && <span className="text-xs bg-yellow-500 text-black px-2 py-0.5 rounded">PRÓXIMO</span>}
                            </div>
                            <p className="text-sm text-gray-400">
                              🎤 {launch.artist_name || 'Sin artista'}
                            </p>
                            <p className="text-xs text-gray-500">
                              📅 {launchDate.toLocaleString("es-DO")}
                            </p>
                            <p className="text-xs text-gray-500">
                              👥 {launch.participants?.length || 0} participantes
                            </p>
                          </div>
                          <Button
                            onClick={() => handleDeleteLaunch(launch.id, launch.title)}
                            data-testid={`admin-delete-launch-${launch.id}`}
                            variant="outline"
                            size="sm"
                            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Eliminar
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Memberships Tab */}
          <TabsContent value="memberships" className="mt-6 space-y-8">
            {/* Pending membership payments (in-app notification) */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CreditCard className="w-6 h-6 text-[#22C55E]" />
                <h2 className="text-xl font-bold">Pagos de Membresía Pendientes ({pendingMemberships.length})</h2>
              </div>

              {pendingMemberships.length === 0 ? (
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="p-6 text-center text-gray-400">
                    No hay pagos de membresía por revisar
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {pendingMemberships.map(m => (
                    <Card key={m.id} data-testid={`membership-pending-${m.id}`} className="bg-[#121212] border-yellow-500/30">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                          <div className="flex-1 min-w-[180px]">
                            <p className="font-bold text-lg">{m.artist_name}</p>
                            <p className="text-lg font-bold text-[#22C55E]">RD${m.amount}</p>
                            <p className="text-sm text-gray-400">
                              {m.months_count} {m.months_count > 1 ? 'meses' : 'mes'}: {(m.months_covered || []).join(", ")}
                            </p>
                            <p className="text-xs text-gray-500">{new Date(m.created_at).toLocaleString("es-DO")}</p>
                          </div>
                          {m.receipt_url && (
                            <button onClick={() => setViewingReceipt(m.receipt_url)} data-testid={`membership-view-receipt-${m.id}`}>
                              <img
                                src={`${BACKEND_URL}${m.receipt_url}`}
                                alt="comprobante"
                                className="w-20 h-20 object-cover rounded-lg border border-white/10 hover:scale-105 transition-transform"
                              />
                            </button>
                          )}
                          <div className="flex flex-col gap-2">
                            <Button
                              onClick={() => handleApproveMembership(m.id, (m.months_count || 1) * 250)}
                              data-testid={`membership-approve-${m.id}`}
                              size="sm"
                              className="bg-[#22C55E] hover:bg-[#22C55E]/80 text-black"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Aprobar
                            </Button>
                            <Button
                              onClick={() => handleRejectMembership(m.id)}
                              data-testid={`membership-reject-${m.id}`}
                              size="sm"
                              variant="outline"
                              className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                            >
                              Rechazar
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Artists membership status (green/red) */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Mic2 className="w-6 h-6 text-[#D946EF]" />
                <h2 className="text-xl font-bold">Estado de Artistas ({artistsMembershipStatus.length})</h2>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {artistsMembershipStatus.map(a => (
                  <Card
                    key={a.artist_id}
                    data-testid={`membership-artist-status-${a.artist_id}`}
                    className={`border ${a.is_paid ? 'border-[#22C55E]/40 bg-[#22C55E]/10' : 'border-red-500/40 bg-red-500/10'}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-bold truncate">{a.artist_name}</p>
                          <p className="text-xs text-gray-400">Mes: {a.current_month}</p>
                          <p className="text-xs text-[#D946EF] font-bold" data-testid={`artist-points-${a.artist_id}`}>{(a.points || 0).toLocaleString()} pts</p>
                          {a.has_pending && !a.is_paid && (
                            <p className="text-[11px] text-yellow-400">Pago en revisión</p>
                          )}
                        </div>
                        <span className={`shrink-0 text-xs font-bold px-2 py-1 rounded-full ${a.is_paid ? 'bg-[#22C55E] text-black' : 'bg-red-500 text-white'}`}>
                          {a.is_paid ? '● PAGADO' : '● NO PAGADO'}
                        </span>
                      </div>
                      <Button
                        onClick={() => handleAddPoints(a.artist_id, a.artist_name)}
                        data-testid={`admin-add-points-${a.artist_id}`}
                        size="sm"
                        variant="outline"
                        className="w-full mt-3 border-[#D946EF]/50 text-[#D946EF] hover:bg-[#D946EF] hover:text-white"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Agregar puntos
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Artist Links Tab */}
          <TabsContent value="artist-links" className="mt-6">
            <div className="flex items-center gap-2 mb-4">
              <Youtube className="w-6 h-6 text-[#FF0000]" />
              <h2 className="text-xl font-bold">Links de Artistas ({artistLinks.length})</h2>
            </div>

            {artistLinks.length === 0 ? (
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-8 text-center text-gray-400">
                  No hay enlaces enviados
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {artistLinks.map(link => (
                  <Card
                    key={link.id}
                    data-testid={`admin-link-card-${link.id}`}
                    className={`border ${link.status === 'reviewed' ? 'border-white/10 bg-[#121212]' : 'border-yellow-500/40 bg-yellow-500/5'}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-bold truncate">{link.title}</p>
                            <span className={`shrink-0 text-[11px] px-2 py-0.5 rounded-full ${link.status === 'reviewed' ? 'bg-[#22C55E] text-black' : 'bg-yellow-500 text-black'}`}>
                              {link.status === 'reviewed' ? 'Revisado' : 'Pendiente'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-400">🎤 {link.artist_name}</p>
                          <a
                            href={link.youtube_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`admin-link-open-${link.id}`}
                            className="text-xs text-[#FF6B6B] hover:underline break-all flex items-center gap-1 mt-1"
                          >
                            <Youtube className="w-4 h-4 shrink-0" />
                            {link.youtube_url}
                          </a>
                          <p className="text-xs text-gray-500 mt-1">{new Date(link.created_at).toLocaleString("es-DO")}</p>
                        </div>
                        <div className="flex flex-col gap-2">
                          {link.status !== 'reviewed' && (
                            <Button
                              onClick={() => handleMarkLinkReviewed(link.id)}
                              data-testid={`admin-link-reviewed-${link.id}`}
                              size="sm"
                              className="bg-[#22C55E] hover:bg-[#22C55E]/80 text-black"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Revisado
                            </Button>
                          )}
                          <Button
                            onClick={() => handleDeleteLink(link.id)}
                            data-testid={`admin-link-delete-${link.id}`}
                            size="sm"
                            variant="outline"
                            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Eliminar
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Inbox (Buzón) Tab */}
          <TabsContent value="inbox" className="mt-6 space-y-8">
            {/* User inbox submissions */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <ClipboardList className="w-6 h-6 text-[#22C55E]" />
                <h2 className="text-xl font-bold">Buzón de Usuarios ({inboxSubmissions.length})</h2>
              </div>
              {/* Point values reference */}
              <div className="flex flex-wrap gap-2 mb-4" data-testid="inbox-point-values-legend">
                {[
                  { k: "combo", l: "Combo" },
                  { k: "suscriptor", l: "Suscripción" },
                  { k: "like_comentario", l: "Like+Coment." },
                  { k: "comentario", l: "Comentario" },
                  { k: "like", l: "Like" },
                  { k: "visita", l: "Visita" },
                ].map(x => (
                  <span key={x.k} className="text-[11px] font-bold px-2 py-1 rounded-md bg-[#161616] border border-white/10 text-gray-300">
                    {x.l}: <span className="text-[#D946EF]">{ACTION_POINT_VALUES[x.k]} pts</span>
                  </span>
                ))}
              </div>
              {inboxSubmissions.length === 0 ? (
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="p-6 text-center text-gray-400">No hay envíos en el buzón</CardContent>
                </Card>
              ) : (
                <div className="space-y-6">
                  {groupedInbox.map(group => (
                    <div key={group.artist_id || 'sin_artista'} data-testid={`inbox-group-${group.artist_id || 'none'}`} className="rounded-xl border border-white/10 bg-[#0d0d0d] overflow-hidden">
                      {/* Artist group header with counters */}
                      <div className="p-3 bg-[#161616] border-b border-white/10">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <p className="font-bold text-white">🎤 {group.artist_name} {group.artist_code && <span className="text-xs text-[#22C55E]">({group.artist_code})</span>}</p>
                          <div className="flex items-center gap-2">
                            <span data-testid={`inbox-count-total-${group.artist_id || 'none'}`} className="text-xs font-bold px-2 py-1 rounded-full bg-white/10 text-gray-200">
                              {group.totalCount} captura{group.totalCount !== 1 ? 's' : ''}
                            </span>
                            {group.pendingCount > 0 && (
                              <span data-testid={`inbox-count-pending-${group.artist_id || 'none'}`} className="text-xs font-bold px-2 py-1 rounded-full bg-yellow-500 text-black animate-pulse">
                                {group.pendingCount} pendiente{group.pendingCount !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Engagement orders progress for this artist */}
                        {group.orders.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {group.orders.map(o => {
                              const total = o.total_units != null ? o.total_units : (o.combos * 15 + o.subscribers + o.comments + o.likes + o.visits);
                              const rec = o.received || 0;
                              const done = total > 0 && rec >= total;
                              return (
                                <span key={o.id} data-testid={`inbox-order-progress-${o.id}`} className={`text-[11px] font-bold px-2 py-1 rounded-md border ${done ? 'border-[#22C55E]/40 bg-[#22C55E]/10 text-[#22C55E]' : 'border-[#D946EF]/30 bg-[#D946EF]/10 text-[#D946EF]'}`}>
                                  {o.order_code || `P-${o.id.slice(0,5).toUpperCase()}`}: {rec}/{total}{done ? ' ✓' : ''}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      {/* Submissions of this artist */}
                      <div className="p-3 space-y-3">
                        {group.subs.map(s => (
                          <Card key={s.id} data-testid={`inbox-sub-${s.id}`} className={`border ${s.status === 'approved' ? 'border-[#22C55E]/40 bg-[#22C55E]/5' : s.status === 'rejected' ? 'border-red-500/30 bg-[#121212]' : 'border-yellow-500/40 bg-yellow-500/5'}`}>
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between gap-4 flex-wrap">
                                <div className="min-w-0 flex-1">
                                  <p className="font-bold">{s.user_name} <span className="text-xs text-gray-400">{s.user_phone}</span></p>
                                  {s.action_type && <p className="text-xs text-[#22C55E] font-bold">Acción: {ACTION_LABELS[s.action_type] || (s.action_type.charAt(0).toUpperCase() + s.action_type.slice(1))} <span className="text-[#D946EF]">({ACTION_POINT_VALUES[s.action_type] ?? '?'} pts)</span></p>}
                                  <p className="text-sm text-gray-300">{s.note || "Sin nota"}</p>
                                  <p className="text-xs text-gray-500">{new Date(s.created_at).toLocaleString("es-DO")}</p>
                                  {s.status === 'approved' && <p className="text-xs text-[#22C55E]">+{s.points_awarded} pts acreditados</p>}
                                </div>
                                {s.screenshot_url && (
                                  <button onClick={() => setViewingReceipt(s.screenshot_url)} data-testid={`inbox-view-${s.id}`}>
                                    <img src={`${BACKEND_URL}${s.screenshot_url}`} alt="captura" className="w-20 h-20 object-cover rounded-lg border border-white/10 hover:scale-105 transition-transform" />
                                  </button>
                                )}
                                {s.status === 'pending' && (
                                  <div className="flex flex-col gap-2 w-full sm:w-auto">
                                    <select
                                      data-testid={`inbox-order-select-${s.id}`}
                                      value={selectedOrderForSub[s.id] || ""}
                                      onChange={(e) => setSelectedOrderForSub(prev => ({ ...prev, [s.id]: e.target.value }))}
                                      className="bg-[#0a0a0a] border border-white/10 rounded-lg text-xs text-white px-2 py-2 max-w-[220px]"
                                    >
                                      <option value="">— Elegir pedido —</option>
                                      {engagementOrders
                                        .filter(o => o.artist_id === s.artist_id)
                                        .map(o => {
                                          const total = o.total_units != null ? o.total_units : (o.combos * 15 + o.subscribers + o.comments + o.likes + o.visits);
                                          const rec = o.received || 0;
                                          return (
                                            <option key={o.id} value={o.id}>
                                              {o.order_code || `P-${o.id.slice(0,5).toUpperCase()}`} ({rec}/{total})
                                            </option>
                                          );
                                        })}
                                    </select>
                                    <Button onClick={() => handleApproveInbox(s.id, selectedOrderForSub[s.id], ACTION_POINT_VALUES[s.action_type])} data-testid={`inbox-approve-${s.id}`} size="sm" className="bg-[#22C55E] hover:bg-[#22C55E]/80 text-black">
                                      <CheckCircle className="w-4 h-4 mr-1" /> Aprobar
                                    </Button>
                                    <Button onClick={() => handleRejectInbox(s.id)} data-testid={`inbox-reject-${s.id}`} size="sm" variant="outline" className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white">
                                      Rechazar
                                    </Button>
                                  </div>
                                )}
                                {s.status !== 'pending' && (
                                  <div className="flex flex-col items-end gap-2 shrink-0">
                                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${s.status === 'approved' ? 'bg-[#22C55E] text-black' : 'bg-red-500 text-white'}`}>
                                      {s.status === 'approved' ? 'Aprobado' : 'Rechazado'}
                                    </span>
                                    {s.status === 'rejected' && (
                                      <Button onClick={() => handleDeleteInbox(s.id)} data-testid={`inbox-delete-${s.id}`} size="sm" variant="outline" className="border-red-500/60 text-red-400 hover:bg-red-500 hover:text-white h-7 px-2 text-xs">
                                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Artist engagement orders */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-6 h-6 text-[#D946EF]" />
                <h2 className="text-xl font-bold">Pedidos de Engagement ({engagementOrders.length})</h2>
              </div>
              {engagementOrders.length === 0 ? (
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="p-6 text-center text-gray-400">No hay pedidos de engagement</CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {engagementOrders.map(o => (
                    <Card key={o.id} data-testid={`engagement-order-${o.id}`} className="bg-[#121212] border-white/10">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="min-w-0 flex-1">
                            <p className="font-bold">🎤 {o.artist_name} <span className="text-[#D946EF]">-{o.points_cost} pts</span></p>
                            <p className="text-xs text-[#D946EF] font-bold">{o.order_code || `P-${o.id.slice(0,5).toUpperCase()}`}</p>
                            <a href={o.link} target="_blank" rel="noopener noreferrer" className="text-xs text-[#FF6B6B] hover:underline break-all">{o.link}</a>
                            <p className="text-xs text-gray-400 mt-1">
                              {o.combos > 0 && `${o.combos} combo(s) · `}
                              {o.subscribers > 0 && `${o.subscribers} subs · `}
                              {o.comments > 0 && `${o.comments} coment. · `}
                              {o.likes > 0 && `${o.likes} likes · `}
                              {o.visits > 0 && `${o.visits} visitas`}
                            </p>
                            {(() => {
                              const total = o.total_units != null ? o.total_units : (o.combos * 15 + o.subscribers + o.comments + o.likes + o.visits);
                              const rec = o.received || 0;
                              const falta = Math.max(0, total - rec);
                              const pct = total > 0 ? Math.min(100, Math.round((rec / total) * 100)) : 0;
                              return (
                                <div className="mt-2" data-testid={`order-progress-${o.id}`}>
                                  <div className="flex gap-3 text-xs font-bold">
                                    <span className="text-white">Pidió: {total}</span>
                                    <span className="text-[#22C55E]">Recibido: {rec}</span>
                                    <span className="text-[#FF6B6B]">Falta: {falta}</span>
                                  </div>
                                  <div className="w-full h-2 bg-[#0a0a0a] rounded-full mt-1 overflow-hidden">
                                    <div className="h-full bg-[#22C55E]" style={{ width: `${pct}%` }} />
                                  </div>
                                </div>
                              );
                            })()}
                            <p className="text-xs text-gray-500 mt-1">{new Date(o.created_at).toLocaleString("es-DO")}</p>
                          </div>
                          <Button onClick={() => handleDeleteOrder(o.id)} data-testid={`order-delete-${o.id}`} size="sm" variant="outline" className="shrink-0 border-red-500/60 text-red-400 hover:bg-red-500 hover:text-white h-8 px-2 text-xs">
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Points Bank Tab */}
          <TabsContent value="points-bank" className="mt-6">
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#FFD700]/20 flex items-center justify-center">
                    <Wallet className="w-6 h-6 text-[#FFD700]" />
                  </div>
                  <div>
                    <span className="text-[#FFD700]">Banco de Puntos</span>
                    <p className="text-3xl font-bold text-white">{(pointsBank.total_points || 0).toLocaleString()} pts</p>
                  </div>
                </CardTitle>
                <CardDescription>
                  Contabilidad de la plataforma. El banco acumula puntos (compra/venta a artistas, comisión de membresía y parte del banco por engagement). Las <span className="text-[#22C55E] font-semibold">Ganancias</span> son el ingreso que genera la plataforma (margen de engagement + comisiones de membresía + venta de puntos a artistas). Los <span className="text-[#FF6B6B]">pagos a usuarios (retiros)</span> se muestran aparte y NO se restan de las Ganancias.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Total earnings (Ganancias) */}
                <div className="mb-6 p-4 rounded-lg bg-[#D946EF]/10 border border-[#D946EF]/30" data-testid="bank-total-earnings">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                      <h3 className="font-semibold text-white flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-[#D946EF]" />
                        Ganancias Totales
                      </h3>
                      <p className="text-xs text-gray-400">Ingreso acumulado de la plataforma (membresías + engagement + venta de puntos)</p>
                      {pointsBank.earnings_reset_at && (
                        <p className="text-[10px] text-gray-500 mt-0.5">Contando desde el {new Date(pointsBank.earnings_reset_at).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                      )}
                    </div>
                    <p className="text-3xl font-bold text-[#D946EF]" data-testid="bank-earnings-total">{(pointsBank.total_earnings || 0).toLocaleString()} pts</p>
                  </div>
                  <Button
                    data-testid="bank-reset-earnings-btn"
                    onClick={handleResetEarnings}
                    variant="outline"
                    className="mt-3 border-[#D946EF]/40 text-[#D946EF] hover:bg-[#D946EF]/10 text-xs h-8"
                  >
                    Reiniciar Ganancias a 0
                  </Button>
                </div>

                {/* Total paid to users (retiros) — shown separately */}
                <div className="mb-6 p-4 rounded-lg bg-[#FF6B6B]/10 border border-[#FF6B6B]/30" data-testid="bank-total-withdrawn">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                      <h3 className="font-semibold text-white flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-[#FF6B6B]" />
                        Total Pagado a Usuarios (retiros)
                      </h3>
                      <p className="text-xs text-gray-400">Suma de todos los pagos a usuarios. Se muestra aparte, no afecta las Ganancias.</p>
                    </div>
                    <p className="text-3xl font-bold text-[#FF6B6B]" data-testid="bank-withdrawn-total">{(pointsBank.total_withdrawn || 0).toLocaleString()} pts</p>
                  </div>
                </div>

                {/* Daily earnings */}
                <div className="mb-6 p-4 rounded-lg bg-[#22C55E]/5 border border-[#22C55E]/20" data-testid="bank-daily-earnings">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                      <h3 className="font-semibold text-white flex items-center gap-2">
                        <Wallet className="w-4 h-4 text-[#22C55E]" />
                        Generado Hoy
                      </h3>
                      <p className="text-xs text-gray-400">Ganancia del día: ingreso de la plataforma (membresías + parte del banco por engagement + venta de puntos)</p>
                    </div>
                    <p className="text-3xl font-bold text-[#22C55E]" data-testid="bank-today-total">+{(bankEarnings.today || 0).toLocaleString()} pts</p>
                  </div>
                  {bankEarnings.history && bankEarnings.history.length > 0 && (
                    <div className="mt-3 space-y-1 max-h-40 overflow-y-auto">
                      {bankEarnings.history.slice(0, 14).map(d => (
                        <div key={d.date} className="flex items-center justify-between text-xs py-1 border-b border-white/5">
                          <span className="text-gray-400">{new Date(d.date + 'T00:00:00').toLocaleDateString('es-DO', { weekday: 'short', day: '2-digit', month: 'short' })}</span>
                          <span className="text-[#22C55E] font-bold">+{d.generated.toLocaleString()} pts</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pricing split — ADMIN ONLY */}
                <div className="mb-6 p-4 rounded-lg bg-[#FFD700]/5 border border-[#FFD700]/20" data-testid="bank-pricing-legend">
                  <h3 className="font-semibold text-white flex items-center gap-2 mb-1">
                    <DollarSign className="w-4 h-4 text-[#FFD700]" />
                    Precios de Engagement (solo admin)
                  </h3>
                  <p className="text-xs text-gray-400 mb-3">Reparto: lo que paga el artista = lo que gana el usuario + la ganancia del banco. La ganancia del banco se registra al <span className="text-[#D946EF]">aprobar la captura en el Buzón</span>.</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead>
                        <tr className="text-gray-400 border-b border-white/10">
                          <th className="py-2 pr-2 font-medium">Acción</th>
                          <th className="py-2 px-2 font-medium text-center">Artista paga</th>
                          <th className="py-2 px-2 font-medium text-center text-[#22C55E]">Usuario gana</th>
                          <th className="py-2 pl-2 font-medium text-center text-[#D946EF]">Banco gana</th>
                        </tr>
                      </thead>
                      <tbody className="text-white">
                        {[
                          { a: 'Comentario', pay: 5, user: 3, bank: 2 },
                          { a: 'Like', pay: 3, user: 2, bank: 1 },
                          { a: 'Visita', pay: 1, user: 1, bank: 0 },
                          { a: 'Suscriptor', pay: 25, user: 15, bank: 10 },
                          { a: 'Like + Comentario', pay: 8, user: 5, bank: 3 },
                          { a: 'Combo (5 usuarios)', pay: 125, user: 100, bank: 25 },
                        ].map((r) => (
                          <tr key={r.a} className="border-b border-white/5">
                            <td className="py-2 pr-2">{r.a}</td>
                            <td className="py-2 px-2 text-center">{r.pay} pts</td>
                            <td className="py-2 px-2 text-center text-[#22C55E]">{r.user} pts</td>
                            <td className="py-2 pl-2 text-center text-[#D946EF]">{r.bank} pts</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Sell points to artist */}
                <div className="mb-6 p-4 rounded-lg bg-[#FFD700]/5 border border-[#FFD700]/20">
                  <h3 className="font-semibold text-white flex items-center gap-2 mb-3">
                    <Wallet className="w-4 h-4 text-[#FFD700]" />
                    Vender Puntos a un Artista
                  </h3>
                  <p className="text-xs text-gray-400 mb-3">
                    Cuando un artista te paga en efectivo, dale puntos aquí. Se registran como ingreso en el banco y se acreditan al artista.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <select
                      data-testid="sell-artist-select"
                      value={sellArtistId}
                      onChange={(e) => setSellArtistId(e.target.value)}
                      className="flex-1 bg-[#0a0a0a] border border-white/10 rounded-lg text-sm text-white px-3 py-2"
                    >
                      <option value="">— Elegir artista —</option>
                      {allArtists.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name} {a.artist_code ? `(${a.artist_code})` : ''}
                        </option>
                      ))}
                    </select>
                    <Input
                      type="number"
                      data-testid="sell-points-input"
                      placeholder="Puntos"
                      value={sellPointsAmount}
                      onChange={(e) => setSellPointsAmount(e.target.value)}
                      className="sm:w-32 bg-[#0a0a0a] border-white/10"
                    />
                    <Button
                      data-testid="sell-points-btn"
                      onClick={handleSellPointsToArtist}
                      disabled={sellingPoints}
                      className="bg-[#FFD700] hover:bg-[#FFD700]/80 text-black font-bold"
                    >
                      {sellingPoints ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Wallet className="w-4 h-4 mr-1" />}
                      Vender
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Total acumulado en el banco: <span className="text-[#FFD700] font-bold">{(pointsBank.total_points || 0).toLocaleString()} pts</span>
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    Últimas Transacciones
                  </h3>
                  {pointsBank.transactions?.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {pointsBank.transactions.map((tx) => (
                        <div key={tx.id} className={`flex items-center justify-between p-3 rounded-lg ${tx.amount > 0 ? 'bg-[#22C55E]/10 border border-[#22C55E]/20' : 'bg-red-500/10 border border-red-500/20'}`}>
                          <div>
                            <p className="text-sm font-medium text-white">
                              {tx.type === 'artist_payment_fee' ? `Comisión de membresía - ${tx.artist_name || 'Artista'}`
                                : tx.type === 'artist_points_purchase' ? `Compra de puntos - ${tx.artist_name || 'Artista'}`
                                : tx.type === 'engagement_bank_share' ? `Ganancia por engagement (${ACTION_LABELS[tx.action_type] || tx.action_type || 'acción'}) - ${tx.artist_name || 'Artista'}`
                                : tx.type === 'user_withdrawal' ? `Retiro de usuario - ${tx.user_name || 'Usuario'} (pago ${tx.net_paid || 0} pts restado de Ganancias)`
                                : tx.type === 'user_deleted' ? `Usuario eliminado - ${tx.user_name || 'Usuario'}`
                                : tx.type === 'sold_to_artist' ? `Venta a artista - ${tx.artist_name || 'Artista'}`
                                : tx.reason || 'Transacción'}
                            </p>
                            <p className="text-xs text-gray-400">
                              {tx.created_at ? new Date(tx.created_at).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                            </p>
                          </div>
                          <span className={`font-bold ${tx.amount > 0 ? 'text-[#22C55E]' : 'text-red-500'}`}>
                            {tx.amount > 0 ? '+' : ''}{tx.amount} pts
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-8">Sin transacciones aún</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Block User Dialog */}
        {showBlockDialog && selectedUser && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <Card className="bg-[#121212] border-white/10 w-full max-w-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-500">
                  <Ban className="w-5 h-5" />
                  Bloquear Usuario
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-400">
                  ¿Estás seguro de bloquear a <strong className="text-white">{selectedUser.name} {selectedUser.last_name}</strong>?
                </p>
                <div className="space-y-2">
                  <Label>Motivo del bloqueo</Label>
                  <Input
                    placeholder="Ej: Violación de términos de uso"
                    className="bg-[#1E1E1E] border-white/10"
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setShowBlockDialog(false);
                      setBlockReason("");
                      setSelectedUser(null);
                    }}
                    variant="outline"
                    className="flex-1 border-white/20"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleBlockUser}
                    className="flex-1 bg-red-500 hover:bg-red-600"
                  >
                    Bloquear
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Reset Password Dialog */}
        {showResetPassword && selectedUser && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <Card className="bg-[#121212] border-white/10 w-full max-w-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#8B5CF6]">
                  <Key className="w-5 h-5" />
                  Resetear Contraseña
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-400">
                  Nueva contraseña para <strong className="text-white">{selectedUser.name} {selectedUser.last_name}</strong>
                </p>
                <div className="space-y-2">
                  <Label>Nueva Contraseña</Label>
                  <Input
                    type="password"
                    placeholder="Mínimo 6 caracteres"
                    className="bg-[#1E1E1E] border-white/10"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setShowResetPassword(false);
                      setNewPassword("");
                      setSelectedUser(null);
                    }}
                    variant="outline"
                    className="flex-1 border-white/20"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleResetPassword}
                    className="flex-1 bg-[#8B5CF6] hover:bg-[#8B5CF6]/80"
                  >
                    Guardar
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* View Receipt Modal — fits entirely on screen, no scroll */}
        {viewingReceipt && (
          <div 
            className="fixed inset-0 bg-black/90 flex flex-col items-center justify-center z-50 p-4"
            onClick={() => setViewingReceipt(null)}
            data-testid="view-receipt-overlay"
          >
            <img 
              src={`${BACKEND_URL}${viewingReceipt}`} 
              alt="Comprobante" 
              className="max-h-[82vh] max-w-[95vw] w-auto h-auto object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <Button
              onClick={() => setViewingReceipt(null)}
              className="mt-4 px-8 bg-white/10 hover:bg-white/20"
              data-testid="view-receipt-close"
            >
              Cerrar
            </Button>
          </div>
        )}

        {/* Artist Reset Password Dialog */}
        {showArtistResetPassword && selectedArtist && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1a1a1a] rounded-xl p-6 max-w-md w-full border border-[#FFD700]/30">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Key className="w-5 h-5 text-[#FFD700]" />
                Restablecer Contraseña del Artista
              </h3>
              <p className="text-gray-400 mb-4">
                Artista: <span className="text-[#D946EF] font-bold">{selectedArtist.name}</span>
              </p>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="artist-new-password">Nueva Contraseña</Label>
                  <Input
                    id="artist-new-password"
                    type="text"
                    placeholder="Mínimo 6 caracteres"
                    value={artistNewPassword}
                    onChange={(e) => setArtistNewPassword(e.target.value)}
                    className="bg-[#121212] border-white/20 mt-1"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 border-white/20"
                    onClick={() => {
                      setShowArtistResetPassword(false);
                      setSelectedArtist(null);
                      setArtistNewPassword("");
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    className="flex-1 bg-gradient-to-r from-[#FFD700] to-[#B8860B] text-black font-bold hover:from-[#B8860B] hover:to-[#FFD700]"
                    onClick={handleResetArtistPassword}
                  >
                    <Key className="w-4 h-4 mr-2" />
                    Cambiar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Artist Launch Date Dialog */}
        {showArtistLaunchDate && selectedArtist && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1a1a1a] rounded-xl p-6 max-w-md w-full border border-[#D946EF]/30">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Rocket className="w-5 h-5 text-[#D946EF]" />
                Fecha de Lanzamiento
              </h3>
              <p className="text-gray-400 mb-4">
                Artista: <span className="text-[#D946EF] font-bold">{selectedArtist.name}</span>
              </p>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="artist-launch-date">Fecha y Hora del Lanzamiento</Label>
                  <Input
                    id="artist-launch-date"
                    type="datetime-local"
                    value={artistLaunchDateTime}
                    onChange={(e) => setArtistLaunchDateTime(e.target.value)}
                    className="bg-[#121212] border-white/20 mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Deja vacío para mostrar "PRONTO"
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 border-white/20"
                    onClick={() => {
                      setShowArtistLaunchDate(false);
                      setSelectedArtist(null);
                      setArtistLaunchDateTime("");
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    className="flex-1 bg-gradient-to-r from-[#D946EF] to-[#8B5CF6] text-white font-bold hover:from-[#8B5CF6] hover:to-[#D946EF]"
                    onClick={handleUpdateArtistLaunchDate}
                  >
                    <Rocket className="w-4 h-4 mr-2" />
                    Guardar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Artist Photo Upload Dialog */}
        {showArtistPhotoUpload && selectedArtist && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1a1a1a] rounded-xl p-6 max-w-md w-full border border-[#22C55E]/30">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-[#22C55E]" />
                Subir Foto del Artista
              </h3>
              <p className="text-gray-400 mb-4">
                Artista: <span className="text-[#22C55E] font-bold">{selectedArtist.name}</span>
              </p>
              
              {/* Current Photo Preview */}
              {selectedArtist.photo_url && (
                <div className="mb-4">
                  <p className="text-sm text-gray-400 mb-2">Foto actual:</p>
                  <img 
                    src={`${BACKEND_URL}${selectedArtist.photo_url}`}
                    alt={selectedArtist.name}
                    className="w-24 h-24 rounded-lg object-cover border border-[#22C55E]/30"
                  />
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="artist-photo">Nueva Foto (JPG, PNG, WebP)</Label>
                  <Input
                    id="artist-photo"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/jpg"
                    onChange={(e) => setArtistPhotoFile(e.target.files[0])}
                    className="bg-[#121212] border-white/20 mt-1"
                  />
                  {artistPhotoFile && (
                    <p className="text-xs text-[#22C55E] mt-1">
                      Archivo seleccionado: {artistPhotoFile.name}
                    </p>
                  )}
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 border-white/20"
                    onClick={() => {
                      setShowArtistPhotoUpload(false);
                      setSelectedArtist(null);
                      setArtistPhotoFile(null);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    className="flex-1 bg-gradient-to-r from-[#22C55E] to-[#16A34A] text-white font-bold hover:from-[#16A34A] hover:to-[#22C55E]"
                    onClick={handleUploadArtistPhoto}
                    disabled={uploadingArtistPhoto || !artistPhotoFile}
                  >
                    {uploadingArtistPhoto ? (
                      <>Subiendo...</>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Subir Foto
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirm.show} onOpenChange={(open) => !open && setDeleteConfirm({ show: false, userId: null, userName: "", type: "user" })}>
          <DialogContent className="bg-[#1a1a1a] border-red-500/50 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-500 flex items-center gap-2">
                <Trash2 className="w-5 h-5" />
                Confirmar Eliminación
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-white">
                ¿Estás seguro de eliminar la cuenta de <strong className="text-red-400">{deleteConfirm.userName}</strong>?
              </p>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300">
                <p className="font-bold mb-1">⚠️ Esta acción es IRREVERSIBLE</p>
                <p>Se eliminarán todos los datos, historial y saldo.</p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-white/20"
                  onClick={() => setDeleteConfirm({ show: false, userId: null, userName: "", type: "user" })}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={confirmDelete}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Screenshot Viewer Dialog */}
        {viewingScreenshot && typeof viewingScreenshot === 'object' && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" onClick={() => setViewingScreenshot(null)}>
            <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
              <Card className="bg-[#121212] border-white/10">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Camera className="w-5 h-5 text-[#8B5CF6]" />
                    Captura de {viewingScreenshot.user_name}
                  </CardTitle>
                  <Button variant="ghost" onClick={() => setViewingScreenshot(null)}>✕</Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-4 text-sm">
                    <div>
                      <p className="text-gray-400">Campaña:</p>
                      <p className="text-white font-bold">{viewingScreenshot.campaign_title || 'YouTube'}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Día:</p>
                      <p className="text-white font-bold">{viewingScreenshot.view_day}/3</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Fecha:</p>
                      <p className="text-white">{new Date(viewingScreenshot.created_at).toLocaleString("es-DO")}</p>
                    </div>
                  </div>
                  <div className="rounded-lg overflow-hidden border border-white/20">
                    <img 
                      src={`${BACKEND_URL}${viewingScreenshot.screenshot_url}`}
                      alt="Captura"
                      className="w-full h-auto max-h-[60vh] object-contain"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button
                      onClick={() => {
                        handleApproveScreenshot(viewingScreenshot.id);
                        setViewingScreenshot(null);
                      }}
                      className="bg-[#22C55E] hover:bg-[#22C55E]/80 text-black px-6"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Aprobar Vista
                    </Button>
                    <Button
                      onClick={() => {
                        handleRejectScreenshot(viewingScreenshot.id, "Captura inválida");
                        setViewingScreenshot(null);
                      }}
                      variant="outline"
                      className="border-red-500 text-red-400 hover:bg-red-500/20 px-6"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Rechazar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
